import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  useGlobalState,
  RealtimeGlobalStateProvider,
  cleanupBlobUrls,
} from '../../../state/RealtimeGlobalContextProvider';
import { type PixelSpacingResult } from '../../../utils/pixelSpacingUpdater';
import { useLocation } from 'react-router-dom';
import {
  FaHourglassEnd,
  FaPause,
  FaPlay,
  FaSpinner,
} from 'react-icons/fa6';
import { TfiLayers } from 'react-icons/tfi';
import l from './index';
import { useSearchParams } from 'react-router-dom';
import SelectView from '../RealtimeInferencingPageV3/components/select-view';
import { appendDerivedBpdLineToStructures } from '../RealtimeInferencingPageV3/utils/realtimeBpdOverlay';
import {
  MediaType,
  mediaTypeMap,
} from '../RealtimeInferencingPageV3/constants';
import { useWebRTC } from '../RealtimeInferencingPageV3/hooks/useWebRTC';
import { buildPlanes } from '../RealtimeInferencingPageV3/utils/planeBuilder';
import { PLANES_CONFIGS } from '../RealtimeInferencingPageV3/config/planes.config';
// import ReportModal from '../../Components/report';
import { useVideoProcessing } from '../../Components/hooks/useVideoProcessing';

import { useNavigate } from 'react-router-dom';
import { useSaveViews } from '../../../helpers/api/realtime/realtime';
import { Icon } from '@ohif/ui';
import { MdOutlineDashboard } from 'react-icons/md';
import { IoIosFlash } from 'react-icons/io';
import { messageI18n } from '../../../helpers/i18n/toastI18n';
import { apiClient } from '../../../helpers/apiHelper';
import StepperFeedRealtime from '../../Components/StepperFeedRealtime';
import FeedPreviewModal from '../../Components/FeedPreviewModal';
import { useHtmlDataTheme } from '../../../services/useHtmlDataTheme';
import { Button } from '../../ComponnentsUI/common/Button';
import { Modal } from '../../ComponnentsUI/common/Modal';
import PatientSelectionModal from '../../ComponnentsUI/PatientSelectionModal/PatientSelectionModal';
// import ReportMedicalPage from '../../../../../../ui/src/components/MeasurementTable/Components/Report/ReportPage';
import { FiFileText } from 'react-icons/fi';
import { ChevronDown } from 'lucide-react';
import { useCreateBlindStudy } from '../../../helpers/api/studies/studies';
import { useCreateBlindInstance } from '../../../helpers/api/instance/instance';
import { fetchCategories, fetchViews } from '../../../helpers/api/config/config';
import { useCategoriesOptional } from '../../../contexts/CategoriesContext';
import { SWEEPS } from '../../ComponnentsUI/NewCaseReverseUpload/constants';
import scansImage from '../../assets/scans_00000.png';

// Blind Sweep in Realtime: keep a separate (non-upload) probe design.
const BlindSweepGuideAnimation = ({ animationClass }: { animationClass: string }) => {
  const pathD =
    (
      {
        // Move vertical guides lower (avoid reaching up to the chest area)
        'ultrasound-sweep-1': 'M 50 92 V 70 M 85 70 H 15',
        'ultrasound-sweep-2': 'M 25 45 V 92',
        'ultrasound-sweep-3': 'M 50 45 V 92',
        'ultrasound-sweep-4': 'M 75 45 V 92',
        'ultrasound-sweep-5': 'M 15 50 H 85',
        'ultrasound-sweep-6': 'M 15 75 H 85',
        'ultrasound-sweep-7': 'M 20 80 Q 50 100 80 80',
      } as Record<string, string>
    )[animationClass] || '';

  return (
    <div className="w-full h-full rounded-md overflow-hidden relative group pointer-events-none bg-slate-200 dark:bg-gray-700">
      <div
        className="absolute inset-0 w-full h-full opacity-50"
        style={{
          backgroundImage: `url(${scansImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />

      <svg className="absolute inset-0 w-full h-full z-10" viewBox="0 0 100 100" preserveAspectRatio="none">
        <path
          d={pathD}
          fill="none"
          stroke="rgba(59, 130, 246, 0.6)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray="4 4"
          className="path-anim"
        />
      </svg>

      {/* Butterfly probe for Realtime Blind Sweep only (centered) */}
      <div className={`absolute z-20 ${animationClass}`}>
        <div style={{ transform: 'translate(-50%, -50%)' }}>
          <div
            style={{
              width: 34,
              height: 18,
              borderRadius: 8,
              background: '#e5e7eb',
              border: '1.2px solid rgba(148,163,184,0.9)',
              boxShadow: '0 6px 18px rgba(0,0,0,0.35)',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: '20%',
                bottom: '20%',
                width: 3,
                background: '#3b82f6',
                borderRadius: '0 3px 3px 0',
                boxShadow: '0 0 10px rgba(59,130,246,0.8)',
              }}
            />
            <div
              style={{
                width: 18,
                height: 9,
                borderRadius: 4,
                border: '1.2px solid rgba(203,213,225,0.9)',
                background: 'rgba(243,244,246,0.95)',
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const normalizeCategoryKey = (value: unknown): string =>
  typeof value === 'string'
    ? value
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '_')
        .replace(/_not_?visible$/gi, '')
    : '';

const TEST_IMAGE_PATH = '/assets/test.png';

declare const ImageCapture: typeof globalThis.ImageCapture;

type BlindSweepState = 'pending' | 'recording' | 'uploading' | 'done' | 'error';

type BlindSweepSegPayload = {
  baseImgSrc: string;
  segs: Record<
    string,
    { points?: string; geometry_type?: string; type?: string; color?: string | null; label?: string }
  >;
};

type BlindSweepFrame = {
  src: string;
  correlationId?: string | null;
  views?: string[];
  qualityByView?: Record<string, any> | null;
};

// Use Upload's `SweepAnimation` directly for 1:1 UI parity.

const RealtimeExamPage: React.FC = () => {
  const { t, i18n: i18nInstance } = useTranslation('RealtimeExam');
  const user = {};
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [sessionEnded, setSessionEnded] = useState(false);
  const navigate = useNavigate();

  const {
    webrtcVideoRef,
    isInferencing,
    setIsInferencing,
    videoMaskStream,
    initializeConnection,
    closeConnection,
    setVideoMaskStream,
    createPeerConnection,
    pcRef,
    dataChannelRef,
    negotiate,
  } = useWebRTC({
    api: process.env.BACKEND_URL,
    videoTransform: 'rotate',
    clientId: user?.profile?.sub,
  });

  const processedVideoRef = useRef<HTMLCanvasElement>(null);
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null);
  // Keep a ref so we can stop capture synchronously (state updates may lag).
  const captureIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const { globalState, setGlobalState } = useGlobalState();
  const {
    videoStream,
    videoRef,
    processingCanvasRef,
    processedStreamRef,
    isVideoLoading,
    createCroppedStream,
    startVideoStream,
    stopVideoStream,
    setIsVideoLoading,
    setVideoStream,
    canvasWidth,
    canvasHeight,
  } = useVideoProcessing(globalState.deviceId);

  // Visible feed element for Blind Sweep (don't reuse `videoRef`, which is used for processing).
  const blindSweepVideoRef = useRef<HTMLVideoElement>(null);

  const theme = useHtmlDataTheme();

  const planes = useMemo(() => {
    return buildPlanes(PLANES_CONFIGS, globalState, theme);
  }, [globalState, theme, i18nInstance.language]);

  const [isModalOpen, setModalOpen] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [bool, setBool] = useState(true);

  const [deviceMediaType, setDeviceMediaType] = useState(
    MediaType.DISPLAY_MEDIA
  );

  const [useDataChannel] = useState(true);
  const [selectedPlansModal, setSelectedPlansModal] = useState('');
  const [selectedPlansTag, setSelectedPlansTag] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [showPrerequisitesModal, setShowPrerequisitesModal] = useState(false);
  const [isInitialSetupComplete, setIsInitialSetupComplete] = useState(false);
  const [showDeviceModal, setShowDeviceModal] = useState(true);
  const [hasStarted, setHasStarted] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const modalCanvasVideoRef = useRef<HTMLCanvasElement>(null);
  const [isVideoBigDisplayOpen, setIsVideoBigDisplayOpen] = useState(false);
  const [showPatientModal, setShowPatientModal] = useState(false);

  // Inline feed selection states
  const [availableFeedSources, setAvailableFeedSources] = useState([]);
  const [pixelSpacingResult, setPixelSpacingResult] = useState<any>(null);

  const [selectedFeedType, setSelectedFeedType] = useState('USB');
  const [selectedFeedSource, setSelectedFeedSource] = useState(''); // Specific USB device ID
  const [machinesList, setMachinesList] = useState([]);
  const [selectedMachineForChange, setSelectedMachineForChange] = useState('');
  const [isApplyingFeedChange, setIsApplyingFeedChange] = useState(false);
  const [showFeedDropdown, setShowFeedDropdown] = useState(false);
  const [showMachineDropdown, setShowMachineDropdown] = useState(false);

  // Feed preview modal states
  const [showFeedPreviewModal, setShowFeedPreviewModal] = useState(false);
  const [previewMachine, setPreviewMachine] = useState(null);
  const [resetStepperOnOpen, setResetStepperOnOpen] = useState(false);

  const [selectedRow, setSelectedRow] = useState<{
    row_id: any;
    patient: any;
  } | null>(null);

  const [showPatientPregnancyModal, setShowPatientPregnancyModal] =
    useState(false);

  const [savedStudyInstanceUID, setSavedStudyInstanceUID] = useState<
    string | null
  >(null);

  // Report modal state
  const [showReportModal, setShowReportModal] = useState(false);

  // ── Mode tabs: Realtime vs Blind Sweep ───────────────────────────────────────
  // Realtime keeps the existing grid-of-views workflow.
  // Blind Sweep reuses the same streaming/inference session, but guides the user through 7 sequential sweeps.
  const [examModeTab, setExamModeTab] = useState<'realtime' | 'blindSweep'>('realtime');
  const [showExamTypeChoice, setShowExamTypeChoice] = useState(false);
  const [examTypeChosen, setExamTypeChosen] = useState(false);
  const [chosenExamMode, setChosenExamMode] = useState<'realtime' | 'blindSweep' | null>(null);

  // ── Blind sweep workflow state (within realtime exam) ────────────────────────
  const BLIND_NUM_SWEEPS = 7;
  const BLIND_SECONDS = 10;
  const [blindStudyUID, setBlindStudyUID] = useState<string | null>(null);
  const [blindActiveSweep, setBlindActiveSweep] = useState<number>(1);
  const [blindSweeps, setBlindSweeps] = useState<
    Array<{
      id: number;
      state: BlindSweepState;
      secondsLeft: number;
      blobUrl?: string | null;
      blob?: Blob | null;
      error?: string | null;
      segmentedFrames?: BlindSweepFrame[];
      latestSegPayload?: BlindSweepSegPayload | null;
    }>
  >(() =>
    Array.from({ length: BLIND_NUM_SWEEPS }, (_, i) => ({
      id: i + 1,
      state: 'pending',
      secondsLeft: BLIND_SECONDS,
      blobUrl: null,
      blob: null,
      error: null,
      segmentedFrames: [],
      latestSegPayload: null,
    }))
  );
  const blindIsFinished = blindSweeps.every((s) => s.state === 'done');
  const [showBlindPatientModal, setShowBlindPatientModal] = useState(false);
  const [blindIsRecording, setBlindIsRecording] = useState(false);
  const [blindIsPaused, setBlindIsPaused] = useState(false);
  const blindIsPausedRef = useRef(false);
  const blindWsAcceptRef = useRef(false);
  // Blind Sweep FPS logging (capture/send cadence)
  const blindFpsRef = useRef<{ t0: number; frames: number; lastLog: number }>({
    t0: 0,
    frames: 0,
    lastLog: 0,
  });
  const blindRecordingStartPerfRef = useRef<number>(0);
  const blindCaptureTickRef = useRef<{ ticks: number; lastLog: number }>({
    ticks: 0,
    lastLog: 0,
  });
  const blindCaptureLoopTickRef = useRef<{ ticks: number; lastLog: number }>({
    ticks: 0,
    lastLog: 0,
  });
  const blindSendDebugRef = useRef<{ tries: number; oks: number; lastLog: number }>({
    tries: 0,
    oks: 0,
    lastLog: 0,
  });
  const blindSendStateRef = useRef<{
    tick: number;
    blockedMeta: number;
    blockedWs: number;
    blockedAccept: number;
    sentOk: number;
    lastLog: number;
  }>({
    tick: 0,
    blockedMeta: 0,
    blockedWs: 0,
    blockedAccept: 0,
    sentOk: 0,
    lastLog: 0,
  });
  const blindVideoReadyWarnRef = useRef<number>(0);
  const blindDebugSnapshotRef = useRef<number>(0);
  const blindOriginalTrackFrameRateRef = useRef<number | null>(null);
  const [blindSelectedFrame, setBlindSelectedFrame] = useState<string | null>(null);
  const [blindShowLabels, setBlindShowLabels] = useState(true);
  const blindShowLabelsRef = useRef(true);
  const [blindAutoFollowLatest, setBlindAutoFollowLatest] = useState(true);
  const recordingSweepIdRef = useRef<number | null>(null);
  const [showBlindVideosModal, setShowBlindVideosModal] = useState(false);
  const [blindVideoSweepId, setBlindVideoSweepId] = useState<number>(1);
  const pendingExamEndModeRef = useRef<'realtime' | 'blindSweep'>('realtime');

  const activeSweepFrames: BlindSweepFrame[] =
    blindSweeps.find((s) => s.id === blindActiveSweep)?.segmentedFrames ?? [];

  const [blindSelectedFrameInfo, setBlindSelectedFrameInfo] = useState<BlindSweepFrame | null>(null);

  // Use a single fixed size for the sweep guide (bigger).
  const blindGuideSize: 'fixed' = 'fixed';
  const blindGuideSweepId = blindIsRecording
    ? (recordingSweepIdRef.current ?? blindActiveSweep)
    : blindActiveSweep;

  const sweepById = useMemo(() => {
    const map = new Map<number, { id: number; name: string; animationClass: string }>();
    (SWEEPS as any[]).forEach((s) => {
      const id = Number(s?.id);
      if (!Number.isNaN(id)) map.set(id, s as any);
    });
    return map;
  }, []);

  const [configCategories, setConfigCategories] = useState<any[]>([]);
  const [configViews, setConfigViews] = useState<any[]>([]);
  const categoriesCtx = useCategoriesOptional();

  // (Frame modal is now image-only; view/criteria modal removed by request.)
  const categoryById = useMemo(() => {
    const map = new Map<number, any>();
    configCategories.forEach((c) => {
      const id = Number(c?.id ?? c?.category_id ?? c?.category_def_id);
      if (!Number.isNaN(id)) map.set(id, c);
    });
    (categoriesCtx?.categories ?? []).forEach((c) => {
      const id = Number((c as any)?.id ?? (c as any)?.category_id ?? (c as any)?.category_def_id);
      if (!Number.isNaN(id)) map.set(id, c);
    });
    return map;
  }, [configCategories, categoriesCtx?.categories]);

  const categoryColorByKey = useMemo(() => {
    const map = new Map<string, string>();
    const addCategoryColor = (category: any) => {
      const color =
        category?.default_color_rgba ??
        category?.color_rgba ??
        category?.color ??
        category?.default_color ??
        null;
      if (typeof color !== 'string' || color.length === 0) return;
      [
        category?.code,
        category?.category_code,
        category?.display_name,
        category?.displayName,
      ].forEach((key) => {
        const normalized = normalizeCategoryKey(key);
        if (normalized) map.set(normalized, color);
      });
    };

    configCategories.forEach(addCategoryColor);
    (categoriesCtx?.categories ?? []).forEach(addCategoryColor);
    return map;
  }, [configCategories, categoriesCtx?.categories]);

  const resolveColorForCategoryId = useCallback(
    (categoryId: number): string | null => {
      if (categoriesCtx?.codeById?.[categoryId]) {
        return categoriesCtx.getColorForCategoryId(categoryId);
      }

      const cat = categoryById.get(categoryId);
      const color =
        cat?.default_color_rgba ??
        cat?.color_rgba ??
        cat?.color ??
        cat?.default_color ??
        null;
      return typeof color === 'string' && color.length > 0 ? color : null;
    },
    [categoriesCtx, categoryById]
  );

  const resolveColorForLabel = useCallback(
    (label?: string | null): string | null => {
      const normalized = normalizeCategoryKey(label);
      return normalized ? categoryColorByKey.get(normalized) ?? null : null;
    },
    [categoryColorByKey]
  );
  // (Blind Sweep "Detected views" panel removed by request.)

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [cats, views] = await Promise.all([fetchCategories(), fetchViews()]);
        if (!mounted) return;
        setConfigCategories(Array.isArray(cats) ? cats : []);
        setConfigViews(Array.isArray(views) ? views : []);
      } catch {
        // If config endpoints fail, we still fall back to segmentation-provided colors.
      }
    })();
    return () => {
      mounted = false;
    };
  }, [globalState?.wsRef]);

  const renderBlindOverlay = useCallback(
    async (
      baseImgSrc: string,
      segs: Record<
        string,
        { points?: string; geometry_type?: string; type?: string; color?: string | null; label?: string }
      >
    ): Promise<string> => {
      const parsePoints = (pointsStr: string) => {
        if (!pointsStr) return [];
        return pointsStr.split(',').map((pair) => {
          const [x, y] = pair.split('\\').map(Number);
          return { x, y };
        });
      };

      const getLabelForCategoryId = (categoryId: number): string | null => {
        const cat = categoryById.get(categoryId);
        const label =
          cat?.display_name ??
          cat?.displayName ??
          cat?.code ??
          cat?.category_code ??
          null;
        if (typeof label === 'string' && label.length > 0) {
          return label;
        }
        return null;
      };

      return await new Promise((resolve, reject) => {
        const imgBase = new Image();
        imgBase.onload = () => {
          try {
            const w = imgBase.width || 1280;
            const h = imgBase.height || 720;
            const c = document.createElement('canvas');
            c.width = w;
            c.height = h;
            const ctx = c.getContext('2d');
            if (!ctx) {
              reject(new Error('No canvas context'));
              return;
            }
            ctx.drawImage(imgBase, 0, 0, w, h);

            ctx.save();
            ctx.lineWidth = 3;
            ctx.globalAlpha = 0.95;
            ctx.setLineDash([8, 6]);
            ctx.lineJoin = 'round';
            ctx.lineCap = 'round';

            const structures = Object.entries(segs).map(([id, seg]) => {
              const pts = parsePoints(seg?.points || '');
              const catId = Number(id);
              const label =
                seg?.label ||
                (Number.isFinite(catId) ? getLabelForCategoryId(catId) : null) ||
                `#${id}`;
              const mappedColor = Number.isFinite(catId)
                ? resolveColorForCategoryId(catId)
                : null;
              const mappedLabelColor = resolveColorForLabel(label);
              const color =
                mappedColor ??
                mappedLabelColor ??
                seg?.color ??
                (() => {
                  const hue = (Number(id) * 47) % 360;
                  return `hsla(${hue}, 85%, 55%, 0.95)`;
                })();
              return {
                label: String(label),
                category_id: Number.isFinite(catId) ? catId : undefined,
                points: pts,
                type: String(seg?.geometry_type ?? seg?.type ?? '').trim().toUpperCase(),
                color,
              };
            });

            appendDerivedBpdLineToStructures(structures, {
              categories: categoriesCtx?.categories ?? [],
              categoriesByCode: categoriesCtx?.categoriesByCode,
              measLabelToCategoryId: categoriesCtx?.measLabelToCategoryId,
            }).forEach((structure) => {
              const pts = structure.points;
              if (!pts.length) return;
              const gType = String(structure.type ?? '').trim().toUpperCase();
              const color = structure.color ?? 'hsla(200, 85%, 55%, 0.95)';

              ctx.strokeStyle = color;
              if (gType === 'ELLIPSE' || gType === 'CIRCLE') {
                const xs = pts.map((p) => p.x);
                const ys = pts.map((p) => p.y);
                const minX = Math.min(...xs);
                const maxX = Math.max(...xs);
                const minY = Math.min(...ys);
                const maxY = Math.max(...ys);
                const centerX = (minX + maxX) / 2;
                const centerY = (minY + maxY) / 2;
                const radiusX = Math.max(1, (maxX - minX) / 2);
                const radiusY = Math.max(1, (maxY - minY) / 2);
                ctx.beginPath();
                ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI);
                ctx.stroke();
              } else if (gType === 'LINE' || gType === 'POLYLINE') {
                ctx.beginPath();
                pts.forEach((pt, idx) => {
                  if (idx === 0) ctx.moveTo(pt.x, pt.y);
                  else ctx.lineTo(pt.x, pt.y);
                });
                ctx.stroke();
              } else {
                ctx.beginPath();
                pts.forEach((pt, idx) => {
                  if (idx === 0) ctx.moveTo(pt.x, pt.y);
                  else ctx.lineTo(pt.x, pt.y);
                });
                ctx.stroke();
              }

              if (blindShowLabelsRef.current) {
                const anchor = pts[0];
                ctx.save();
                ctx.setLineDash([]);
                ctx.globalAlpha = 1;
                ctx.font =
                  'bold 16px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
                const text = String(structure.label).split('_').join(' ');
                const padX = 6;
                const padY = 4;
                const metrics = ctx.measureText(text);
                const boxW = Math.ceil(metrics.width) + padX * 2;
                const boxH = 22 + padY;
                const x = Math.max(0, Math.min(w - boxW, anchor.x + 6));
                const y = Math.max(boxH, Math.min(h - 2, anchor.y));
                ctx.fillStyle = 'rgba(0,0,0,0.55)';
                ctx.fillRect(x, y - boxH, boxW, boxH);
                ctx.fillStyle = color;
                ctx.fillText(text, x + padX, y - 8);
                ctx.restore();
              }
            });
            ctx.restore();

            resolve(c.toDataURL('image/jpeg', 0.92));
          } catch (e) {
            reject(e);
          }
        };
        imgBase.onerror = (e) => reject(e);
        imgBase.src = baseImgSrc;
      });
    },
    [categoryById, resolveColorForCategoryId, resolveColorForLabel, categoriesCtx]
  );

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingChunksRef = useRef<BlobPart[]>([]);
  const blindCountdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { mutateAsync: createBlindStudyAsync, isPending: creatingBlindStudy } =
    useCreateBlindStudy(() => {}, () => {}) as any;
  const { mutateAsync: createBlindInstanceAsync } =
    useCreateBlindInstance(() => {}, () => {}) as any;

  const onSuccessSaveViews = (response, data) => {
    const studyInstanceUID = response?.data?.study_instance_uid;
    if (studyInstanceUID) {
      setSavedStudyInstanceUID(studyInstanceUID);
    }

    // Blind Sweep: after DICOM/views are created, upload recorded sweep videos one-by-one.
    // v3 endpoint: attach videos to the SAME existing study (no inference / no frame extraction).
    if (pendingExamEndModeRef.current === 'blindSweep' && studyInstanceUID) {
      (async () => {
        try {
          // Upload sweeps using backend SweepPayload contract:
          // - First request: no upload_session_id; includes existing_patient_id + studies manifest
          // - Subsequent: upload_session_id + file_key
          let uploadSessionId: string | null = null;
          const existingStudyUid = String(studyInstanceUID);

          const expectedVideoFiles = blindSweeps
            .filter((s) => !!s?.blob)
            .map((s) => ({ file_name: `sweep_${s.id}.webm` }));

          for (const sweep of blindSweeps) {
            if (!sweep?.blob) continue;
            const sweepId = sweep.id;
            setBlindSweeps((prev) =>
              prev.map((s) =>
                s.id === sweepId ? { ...s, state: 'uploading', error: null } : s
              )
            );

            const fileName = `sweep_${sweepId}.webm`;
            const file = new File([sweep.blob], fileName, {
              type: sweep.blob.type || 'video/webm',
            });
            const formData = new FormData();
            // Use legacy key "file"; backend will use payload.file_key as the real key.
            formData.append('file', file);

            const payload: any = {
              study_instance_uid: existingStudyUid,
              file_key: fileName,
              examType: 'BLIND',
              studyCategory: null,
              video_meta: {
                file_name: fileName,
              },
            };
            if (uploadSessionId) {
              payload.upload_session_id = uploadSessionId;
            } else {
              // Optional manifest (helps backend compute missing_keys).
              payload.studies = [
                {
                  examType: 'BLIND',
                  studyCategory: null,
                  sop_instance_uids: [],
                  assigned_video_files: expectedVideoFiles,
                },
              ];
            }
            formData.append('payload', JSON.stringify(payload));

            // Backend unified sweep upload is mounted under `/api/v1`.
            const res: any = await apiClient.postUpload(
              '/upload/workflow/unified-case/sweep_v3',
              formData
            );
            const nextUploadSessionId =
              res?.data?.result?.upload_session_id ??
              res?.data?.result?.uploadSessionId ??
              res?.result?.upload_session_id ??
              res?.result?.uploadSessionId ??
              res?.upload_session_id ??
              null;
            if (!uploadSessionId && nextUploadSessionId) {
              uploadSessionId = String(nextUploadSessionId);
            }

            setBlindSweeps((prev) =>
              prev.map((s) => (s.id === sweepId ? { ...s, state: 'done' } : s))
            );
          }
          messageI18n.success('realtime.blindSweepVideosUploaded');
        } catch (e: any) {
          console.error('[BlindSweep] video upload failed:', e);
          messageI18n.error('realtime.blindSweepVideosUploadFailed');
          // Mark the first pending upload as error (best-effort)
          setBlindSweeps((prev) =>
            prev.map((s) =>
              s.state === 'uploading' ? { ...s, state: 'error', error: 'Upload failed' } : s
            )
          );
        }
      })();
    }

    updateGlobalStateWithFrameIds(
      globalState,
      response?.data?.result?.report?.data?.frame_view_mapping
    );
    setSessionEnded(true);
  };

  const onErrorSaveViews = (error, data) => {
    setSessionEnded(true);
  };

  const { mutate: saveViews, isPending: saveViewsLoading } = useSaveViews(
    onSuccessSaveViews,
    onErrorSaveViews
  );

  console.log('isInferencing :::', isInferencing, videoRef);
  const realtimeExamDuration = 1000;

  interface CropRegion {
    width: number;
    height: number;
    x: number;
    y: number;
  }

  const covertToFrontendCropping = (cropBox: number[]): CropRegion => {
    const [left, upper, right, lower] = cropBox;
    console.log('cropBox ::::', cropBox);
    return {
      width: left,
      height: upper,
      x: right,
      y: lower,
    };
  };

  const videoWidth = videoRef?.current?.videoWidth;
  const videoHeight = videoRef?.current?.videoHeight;
  const cropBox =
    globalState?.machineConfig &&
      (globalState.machineConfig.crop_height != null ||
        globalState.machineConfig.crop_width != null ||
        globalState.machineConfig.crop_x != null ||
        globalState.machineConfig.crop_y != null)
      ? [
        globalState.machineConfig.crop_height ?? 0,
        globalState.machineConfig.crop_width ?? 0,
        globalState.machineConfig.crop_x ?? 0,
        globalState.machineConfig.crop_y ?? 0,
      ]
      : [0, 0, videoWidth ?? 0, videoHeight ?? 0];

  let CROP_REGION = covertToFrontendCropping(cropBox);
  console.log('CROP_REGION ::::', CROP_REGION);
  const [croppedStream, setCroppedStream] = useState<MediaStream | null>(null);


  const captureFrame = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');

    const logSnapshot = (reason: string) => {
      if (examModeTab !== 'blindSweep') return;
      const now = Date.now();
      if (now - blindDebugSnapshotRef.current < 1000) return; // throttle
      blindDebugSnapshotRef.current = now;
      const svc: any = globalState?.wsRef?.current;
      const rs = svc?.socket?.readyState;
      console.log(`[BlindSweep][captureFrame] ${reason}`, {
        isRecording: blindIsRecording,
        isPausedRef: blindIsPausedRef.current,
        accept: blindWsAcceptRef.current,
        isVideoFrozen: globalState?.isVideoFrozen,
        wsReadyState: rs,
        wsConnected: !!globalState?.wsRef?.current?.isConnected?.(),
        videoReadyState: video?.readyState ?? 'n/a',
        videoSize: `${video?.videoWidth ?? 0}x${video?.videoHeight ?? 0}`,
        hasStream: !!(video as any)?.srcObject,
      });
    };

    // Blind Sweep: count capture ticks even if WS/video metadata isn't ready yet.
    if (examModeTab === 'blindSweep' && blindIsRecording && !blindIsPausedRef.current) {
      const now = performance.now();
      blindCaptureTickRef.current.ticks += 1;
      if (blindCaptureTickRef.current.lastLog === 0) {
        blindCaptureTickRef.current.lastLog = now;
      }
      // Log every ~2s to show whether capture loop is actually running.
      if (now - blindCaptureTickRef.current.lastLog >= 2000) {
        console.log(
          `[BlindSweep] capture ticks=${blindCaptureTickRef.current.ticks} videoReadyState=${video?.readyState ?? 'n/a'} videoSize=${video?.videoWidth ?? 0}x${video?.videoHeight ?? 0} hasStream=${!!(video as any)?.srcObject} hasWS=${!!globalState?.wsRef?.current}`
        );
        blindCaptureTickRef.current.lastLog = now;
      }
    }

    if (!video || !context || !canvas) {
      if (examModeTab === 'blindSweep' && blindIsRecording && !blindIsPausedRef.current) {
        console.log('[BlindSweep] captureFrame blocked: missing video/canvas/context');
        logSnapshot('blocked:missing-elements');
      }
      return;
    }

    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;

    if (!videoWidth || !videoHeight) {
      if (examModeTab === 'blindSweep' && blindIsRecording && !blindIsPausedRef.current) {
        blindSendStateRef.current.blockedMeta += 1;
        const now = Date.now();
        // Throttle this log; also try to kick playback.
        if (now - blindVideoReadyWarnRef.current > 1500) {
          console.log(
            `[BlindSweep] captureFrame blocked: video metadata not ready (readyState=${video.readyState})`
          );
          blindVideoReadyWarnRef.current = now;
        }
        logSnapshot('blocked:video-metadata');
        if (video.paused) {
          void video.play().catch(() => undefined);
        }
      }
      return;
    }

    // Blind Sweep: log effective capture FPS while recording (based on successful frames)
    if (
      examModeTab === 'blindSweep' &&
      blindIsRecording &&
      !blindIsPausedRef.current
    ) {
      const now = performance.now();
      if (blindFpsRef.current.t0 === 0) {
        blindFpsRef.current.t0 = now;
        blindFpsRef.current.lastLog = now;
        blindFpsRef.current.frames = 0;
        console.log('[BlindSweep FPS] first frame tick');
      }
      blindFpsRef.current.frames += 1;
      // Log every ~2s to avoid console spam
      if (now - blindFpsRef.current.lastLog >= 2000) {
        const elapsed = (now - blindFpsRef.current.t0) / 1000;
        const fps = elapsed > 0 ? blindFpsRef.current.frames / elapsed : 0;
        console.log(
          `[BlindSweep FPS] capture/send ~${fps.toFixed(2)} fps | video readyState=${video.readyState} | size=${videoWidth}x${videoHeight}`
        );
        blindFpsRef.current.lastLog = now;
      }
    } else if (examModeTab === 'blindSweep' && !blindIsRecording) {
      // Reset when not recording so next sweep starts fresh
      blindFpsRef.current.t0 = 0;
      blindFpsRef.current.frames = 0;
      blindFpsRef.current.lastLog = 0;
    }

    // Some browsers won't render frames until playback starts.
    // Ensure the element is playing (safe no-op if already playing).
    if (video.paused) {
      void video.play().catch(() => undefined);
    }

    // Return to the old working approach:
    // 1) Draw the FULL native frame (no client-side crop/resize).
    // 2) Send raw binary + ROI config so the backend crops consistently.
    canvas.width = videoWidth;
    canvas.height = videoHeight;
    context.drawImage(video, 0, 0, videoWidth, videoHeight);

    if (!globalState?.wsRef.current?.isConnected?.()) {
      // Match realtime: connection lifecycle is managed by the global provider.
      if (examModeTab === 'blindSweep') {
        blindSendStateRef.current.blockedWs += 1;
        logSnapshot('blocked:ws-not-connected');
      }
      return;
    }

    // Build ROI config for server-side cropping (same as old realtime).
    let roiConfig: { crop_x: number; crop_y: number; crop_width: number; crop_height: number } | null = null;
    if (globalState?.machineConfig) {
      const mc = globalState.machineConfig;
      const hasCropConfig =
        mc.crop_x != null ||
        mc.crop_y != null ||
        mc.crop_width != null ||
        mc.crop_height != null;
      if (hasCropConfig) {
        roiConfig = {
          crop_x: Math.max(0, Math.min(videoWidth, mc.crop_x ?? 0)),
          crop_y: Math.max(0, Math.min(videoHeight, mc.crop_y ?? 0)),
          crop_width: Math.max(
            0,
            Math.min(videoWidth - (mc.crop_x ?? 0), mc.crop_width ?? videoWidth)
          ),
          crop_height: Math.max(
            0,
            Math.min(videoHeight - (mc.crop_y ?? 0), mc.crop_height ?? videoHeight)
          ),
        };
      }
    }

    // Blind Sweep: never send frames unless we explicitly accept WS traffic
    // (recording window only; Pause/Stop/Finish must stop sending immediately).
    if (examModeTab === 'blindSweep' && !blindWsAcceptRef.current) {
      blindSendStateRef.current.blockedAccept += 1;
      console.log('[BlindSweep] captureFrame blocked: blindWsAcceptRef=false');
      logSnapshot('blocked:accept-false');
      return;
    }

    // Prefer binary frames (preserves pixels, avoids base64 overhead); fallback to legacy base64.
    const svc: any = globalState.wsRef.current as any;
    const contentType = 'image/png';
    const sendLegacyBase64 = () => {
      const frameData = canvas.toDataURL('image/jpeg', 0.8);
      return svc.sendFrame(frameData, '1', globalState.sessionId, roiConfig);
    };

    // Binary send is async; but our loop is tick-based so fire-and-forget is OK.
    if (typeof svc.sendFrameBinary === 'function' && typeof canvas.toBlob === 'function') {
      void new Promise<void>((resolve) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              const okLegacy = sendLegacyBase64();
              if (okLegacy && examModeTab === 'blindSweep') blindSendStateRef.current.sentOk += 1;
              if (examModeTab === 'blindSweep') logSnapshot(okLegacy ? 'sent:ok' : 'sent:failed');
              return resolve();
            }
            void svc
              .sendFrameBinary(blob, '1', contentType, globalState.sessionId, roiConfig)
              .then((okBin: boolean) => {
                if (okBin && examModeTab === 'blindSweep') blindSendStateRef.current.sentOk += 1;
                if (examModeTab === 'blindSweep') logSnapshot(okBin ? 'sent:ok' : 'sent:failed');
                resolve();
              })
              .catch(() => {
                const okLegacy = sendLegacyBase64();
                if (okLegacy && examModeTab === 'blindSweep') blindSendStateRef.current.sentOk += 1;
                if (examModeTab === 'blindSweep') logSnapshot(okLegacy ? 'sent:ok' : 'sent:failed');
                resolve();
              });
          },
          contentType,
          1.0
        );
      });
    } else {
      const okLegacy = sendLegacyBase64();
      if (okLegacy && examModeTab === 'blindSweep') blindSendStateRef.current.sentOk += 1;
      if (examModeTab === 'blindSweep') logSnapshot(okLegacy ? 'sent:ok' : 'sent:failed');
    }

    if (examModeTab === 'blindSweep' && blindIsRecording && !blindIsPausedRef.current) {
      blindSendDebugRef.current.tries += 1;
      // oks is updated in the send callbacks above
      const now = performance.now();
      if (blindSendDebugRef.current.lastLog === 0) blindSendDebugRef.current.lastLog = now;
      if (now - blindSendDebugRef.current.lastLog >= 2000) {
        const svc: any = globalState?.wsRef?.current;
        const rs = svc?.socket?.readyState;
        console.log(
          `[BlindSweep] send stats: ok=${blindSendDebugRef.current.oks}/${blindSendDebugRef.current.tries} socketReadyState=${rs}`
        );
        blindSendDebugRef.current.lastLog = now;
      }
    }
    // Legacy "ok" is handled above; don't double-warn here.
  }, [
    examModeTab,
    blindIsRecording,
    globalState?.machineConfig,
    globalState?.sessionId,
    globalState?.wsRef,
  ]);

  const ensureBlindProcessingVideoReady = useCallback(async () => {
    const v = videoRef.current;
    if (!v || !videoStream) return;

    try {
      // Force-refresh srcObject to kick metadata in some browsers.
      if (v.srcObject !== videoStream) {
        v.srcObject = videoStream;
      } else {
        // Re-assign to trigger metadata if it got stuck
        v.srcObject = null;
        v.srcObject = videoStream;
      }
      await v.play().catch(() => undefined);
    } catch {}

    // Wait briefly for metadata to become available (up to 1s).
    await new Promise<void>((resolve) => {
      const start = performance.now();
      const done = () => resolve();
      const check = () => {
        if (!videoRef.current) return done();
        if (videoRef.current.videoWidth > 0 && videoRef.current.videoHeight > 0) return done();
        if (performance.now() - start > 1000) return done();
        requestAnimationFrame(check);
      };
      // Also listen to loadedmetadata once.
      try {
        const handler = () => {
          try { v.removeEventListener('loadedmetadata', handler); } catch {}
          done();
        };
        v.addEventListener('loadedmetadata', handler, { once: true } as any);
      } catch {}
      check();
    });
  }, [videoStream, videoRef]);

  // Blind Sweep capture loop is managed by the WS/capture effect (same as Realtime).


/////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////
  

  // const [testImageBlob, setTestImageBlob] = useState<Blob | null>(null);
  // const [testImageDimensions, setTestImageDimensions] = useState<{width: number, height: number} | null>(null);

  // useEffect(() => {
  //   const fetchTestImage = async () => {
  //     try {
  //       const response = await fetch(TEST_IMAGE_PATH);
  //       if (!response.ok) {
  //         throw new Error(`Failed to fetch: ${response.status}`);
  //       }
  //       const blob = await response.blob();
        
  //       // Store raw blob directly - no base64 conversion needed!
  //       setTestImageBlob(blob);
  //       console.log('✅ Test image blob ready:', blob.size, 'bytes, type:', blob.type);

  //       // Get image dimensions
  //       const img = new Image();
  //       img.onload = () => {
  //         setTestImageDimensions({ width: img.width, height: img.height });
  //         console.log('✅ Test image dimensions:', img.width, 'x', img.height);
  //       };
  //       img.src = TEST_IMAGE_PATH;
  //     } catch (err) {
  //       console.error('❌ Failed to load test image:', err);
  //     }
  //   };
  //   fetchTestImage();
  // }, []);

  // const captureFrame = useCallback(() => {
  //   if (!globalState?.wsRef.current) return;
    
  //   if (!testImageBlob || !testImageDimensions) {
  //     console.log('Test image not loaded yet...');
  //     return;
  //   }

  //   // Build ROI config for backend server-side extraction
  //   let roiConfig: { crop_x: number; crop_y: number; crop_width: number; crop_height: number } | null = null;
  //   if (globalState?.machineConfig) {
  //     const mc = globalState.machineConfig;
  //     const hasCropConfig =
  //       mc.crop_x != null ||
  //       mc.crop_y != null ||
  //       mc.crop_width != null ||
  //       mc.crop_height != null;
  //     if (hasCropConfig) {
  //       roiConfig = {
  //         // crop_x: mc.crop_x ?? 0,
  //         // crop_y: mc.crop_y ?? 0,
  //         // crop_width: mc.crop_width ?? testImageDimensions.width,
  //         // crop_height: mc.crop_height ?? testImageDimensions.height,
  //       };
  //     }
  //   }

  //   // Get content type from blob (preserves original format)
  //   const contentType = testImageBlob.type || 'image/png';

  //   console.log('📤 Sending binary frame:', testImageBlob.size, 'bytes, type:', contentType, 'ROI:', roiConfig);
    
  //   // Send as binary (raw bytes, no base64 encoding)
  //   globalState.wsRef.current.sendFrameBinary(
  //     testImageBlob,
  //     '1',
  //     contentType,
  //     globalState.sessionId,
  //     roiConfig
  //   );
  // }, [globalState?.machineConfig, testImageBlob, testImageDimensions, globalState?.sessionId, globalState?.wsRef]);
  
  const isVideoBigDisplayOpenRef = useRef(isVideoBigDisplayOpen);
  const lastFrameTime = Date.now();
  const frameTimeout = 5000;

  useEffect(() => {
    isVideoBigDisplayOpenRef.current = isVideoBigDisplayOpen;
  }, [isVideoBigDisplayOpen]);

  useEffect(() => {
    blindIsPausedRef.current = blindIsPaused;
  }, [blindIsPaused]);

  useEffect(() => {
    blindShowLabelsRef.current = blindShowLabels;
  }, [blindShowLabels]);

  useEffect(() => {
    // When switching sweeps, show the latest segmented frame for that sweep.
    // If there are no frames yet, clear the preview.
    const frames =
      blindSweeps.find((s) => s.id === blindActiveSweep)?.segmentedFrames ?? [];
    if (frames.length > 0) {
      setBlindSelectedFrame(frames[0].src);
      setBlindSelectedFrameInfo(frames[0]);
    } else {
      setBlindSelectedFrame(null);
      setBlindSelectedFrameInfo(null);
    }
    // Follow latest again when user changes sweep manually.
    setBlindAutoFollowLatest(true);
  }, [blindActiveSweep]);

  useEffect(() => {
    // Re-render the CURRENT preview immediately when toggling labels.
    const s = blindSweeps.find((x) => x.id === blindActiveSweep);
    const latest = s?.latestSegPayload ?? null;
    if (!latest) return;
    if (blindIsPausedRef.current) return;
    // If user is browsing an older frame, we don't force-jump; only re-render when auto-following.
    if (!blindAutoFollowLatest) return;
    renderBlindOverlay(latest.baseImgSrc, latest.segs)
      .then((rendered) => setBlindSelectedFrame(rendered))
      .catch(() => undefined);
  }, [blindShowLabels, blindActiveSweep, blindSweeps, blindAutoFollowLatest, renderBlindOverlay]);

  // Initialize sessionId when exam starts
  useEffect(() => {
    if (globalState.isStart && !globalState.sessionId) {
      const randomSessionId =
        Math.random().toString(36).substring(2, 15) +
        Math.random().toString(36).substring(2, 15) +
        Math.random().toString(36).substring(2, 15) +
        Date.now().toString(36);

      console.log(
        '🎯 Initializing sessionId for realtime exam:',
        randomSessionId
      );
      setGlobalState((prevState) => ({
        ...prevState,
        sessionId: randomSessionId,
      }));
    }
  }, [globalState.isStart, globalState.sessionId]);

  useEffect(() => {
    if (globalState?.approach === 'newWS' && globalState?.wsRef?.current) {
      
      globalState.wsRef.current.setOnPixelSpacingCallback(
        (pixelSpacingData: PixelSpacingResult) => {
          try {
            const correlationId = pixelSpacingData?.correlation_id;
            
            if (!correlationId) {
              return;
            }
            
            
            setPixelSpacingResult(pixelSpacingData);

            setGlobalState((prevState) => ({
              ...prevState,
              pixelSpacingMap: {
                ...prevState.pixelSpacingMap,
                [correlationId]: {
                  estimated_mm_per_pixel: pixelSpacingData.data?.estimated_mm_per_pixel ?? null,
                  spacing_px_mode: pixelSpacingData.data?.spacing_px_mode ?? null,
                  det_conf: pixelSpacingData.data?.det_conf ?? 0,
                  roi_xyxy: pixelSpacingData.data?.roi_xyxy ?? [],
                  dashes_selected: pixelSpacingData.data?.dashes_selected ?? 0,
                  dashes_after_alignment: pixelSpacingData.data?.dashes_after_alignment ?? 0,
                  mode_count: pixelSpacingData.data?.mode_count ?? 0,
                  best_area_key: pixelSpacingData.data?.best_area_key ?? null,
                  ok: pixelSpacingData.data?.ok ?? pixelSpacingData.status === 'success',
                  status: pixelSpacingData.status,
                  message: pixelSpacingData.message ?? '',
                  datetime: pixelSpacingData.datetime ?? '',
                },
              },
            }));

            window.dispatchEvent(
              new CustomEvent('pixelSpacingResult', {
                detail: pixelSpacingData,
              })
            );
          } catch (error) {
            console.error('Error processing pixel spacing callback:', error);
          }
        }
      );

      return () => {
        if (globalState?.wsRef?.current) {
          console.log('🔌 Removing pixel spacing listener on WebSocket change');
          globalState.wsRef.current.setOnPixelSpacingCallback(null);
        }
      };
    }
  }, [globalState?.approach, globalState?.wsRef]);

  useEffect(() => {
    let interval123: NodeJS.Timeout | null = null;
    const setupWebRTC = async () => {
      console.log(bool, 'bool value in useEffect', videoStream);
      if (!globalState.isVideoFrozen && globalState?.isStart) {
        const newCroppedStream = createCroppedStream(videoStream, CROP_REGION);
        setCroppedStream(newCroppedStream);
        setIsInferencing(true);

        if (globalState?.approach === 'newWS') {
          // Blind Sweep: when not recording, fully detach frame callback so we don't keep receiving.
          if (examModeTab === 'blindSweep' && !blindIsRecording) {
            if (intervalId) clearInterval(intervalId as any);
            setIntervalId(null);
            globalState?.wsRef.current.setOnFrameCallback(null);
            return;
          }

          // Interval creation (single source of truth, same semantics as realtime pause/resume):
          // - Realtime: capture continuously while started (after exam type chosen).
          // - Blind Sweep: capture only while recording and not paused.
          const shouldCapture = examTypeChosen
            ? (examModeTab === 'blindSweep'
                ? (blindIsRecording && !blindIsPaused)
                : true)
            : false;
          const fpsMs = 1000;
          if (intervalId) clearInterval(intervalId as any);
          if (captureIntervalRef.current) clearInterval(captureIntervalRef.current as any);
          const interval = shouldCapture ? setInterval(captureFrame, fpsMs) : null;
          setIntervalId(interval);
          captureIntervalRef.current = interval;

          globalState?.wsRef.current.setOnFrameCallback(
            (frameData: string) => {
              // Blind Sweep: when not recording, stop receiving/processing WS frames.
              // This prevents "continued receiving" after pressing Stop (buffered messages).
              if (examModeTab === 'blindSweep' && !blindWsAcceptRef.current) return;
              let parsedMessage: any = null;
              try {
                parsedMessage = JSON.parse(frameData);
              } catch (e) {
                // Ignore malformed messages
                return;
              }

              const data = parsedMessage?.data;
              const correlationId = parsedMessage?.correlation_id;

              // Backends differ: sometimes `data` is a string dataURL, sometimes `data` is an object with `image`.
              // If backend doesn't return a pre-rendered overlay, we can draw segmentations client-side (AI dashed style).
              const baseImgSrc: string | null =
                typeof data === 'string'
                  ? data
                  : typeof data?.image === 'string'
                    ? data.image
                    : typeof parsedMessage?.image === 'string'
                      ? parsedMessage.image
                      : null;

              let overlayImgSrc: string | null =
                typeof data === 'string'
                  ? data
                  : typeof data?.image === 'string'
                    ? data.image
                    : typeof parsedMessage?.image === 'string'
                      ? parsedMessage.image
                      : null;

              let pushedBlindFrame = false;

              // If we have segmentations but no overlay, draw them onto the base frame.
              // (Used by Blind Sweep preview/strip; safe for realtime too.)
              if (
                baseImgSrc &&
                data?.frames?.['0']?.segmentations &&
                typeof data?.frames?.['0']?.segmentations === 'object'
              ) {
                try {
                  // If segmentations are present, we only want to show the rendered (drawn) overlay in Blind Sweep.
                  // Rendering happens async (image onload), so mark this tick as "handled" now to avoid pushing the raw frame.
                  pushedBlindFrame = true;

                  const segs = data.frames['0'].segmentations as Record<
                    string,
                    { points?: string; geometry_type?: string; type?: string; color?: string | null; label?: string }
                  >;
                  renderBlindOverlay(baseImgSrc, segs)
                    .then((rendered) => {
                      overlayImgSrc = rendered;
                      const recordingSweepId = recordingSweepIdRef.current;
                      const frameViews: string[] =
                        (typeof data !== 'string' &&
                          (((data as any)?.frames?.['0']?.views as any) ??
                            ((data as any)?.views as any))) ||
                        [];
                      const frameQualityByView =
                        (typeof data !== 'string' &&
                          (((data as any)?.frames?.['0']?.quality_by_view as any) ??
                            ((data as any)?.frames?.['0']?.qualityByView as any) ??
                            ((data as any)?.frames?.['0']?.quality_criteria_by_view as any) ??
                            ((data as any)?.frames?.['0']?.qualityCriteriaByView as any))) ||
                        null;
                      if (
                        examModeTab === 'blindSweep' &&
                        blindIsRecording &&
                        !blindIsPausedRef.current &&
                        recordingSweepId
                      ) {
                        setBlindSweeps((prev) =>
                          prev.map((s) => {
                            if (s.id !== recordingSweepId) return s;
                            const nextFrames: BlindSweepFrame[] = [
                              {
                                src: rendered,
                                correlationId,
                                views: Array.isArray(frameViews) ? frameViews : [],
                                qualityByView:
                                  frameQualityByView && typeof frameQualityByView === 'object'
                                    ? (frameQualityByView as any)
                                    : null,
                              },
                              ...((s.segmentedFrames as any) ?? []),
                            ].slice(0, 60);
                            return {
                              ...s,
                              segmentedFrames: nextFrames,
                              latestSegPayload: { baseImgSrc, segs },
                            };
                          })
                        );
                        if (blindAutoFollowLatest) {
                          const next: BlindSweepFrame = {
                            src: rendered,
                            correlationId,
                            views: Array.isArray(frameViews) ? frameViews : [],
                            qualityByView:
                              frameQualityByView && typeof frameQualityByView === 'object'
                                ? (frameQualityByView as any)
                                : null,
                          };
                          setBlindSelectedFrame(rendered);
                          setBlindSelectedFrameInfo(next);
                        }
                      }
                    })
                    .catch(() => undefined);
                } catch {
                  // If overlay render fails, fall back to base image.
                }
              }

              if (overlayImgSrc) {
                const img = new Image();
                img.onload = () => {
                  const ctx = processedVideoRef.current?.getContext('2d');
                  if (ctx && processedVideoRef.current) {
                    ctx.drawImage(
                      img,
                      0,
                      0,
                      processedVideoRef.current.width,
                      processedVideoRef.current.height
                    );
                  }
                  if (isVideoBigDisplayOpenRef.current) {
                    const ctxmodal =
                      modalCanvasVideoRef.current?.getContext('2d');
                    if (ctxmodal && modalCanvasVideoRef.current) {
                      ctxmodal.drawImage(
                        img,
                        0,
                        0,
                        modalCanvasVideoRef.current.width,
                        modalCanvasVideoRef.current.height
                      );
                    }
                  }
                };
                img.src = overlayImgSrc;
              }

              // Blind Sweep: build a strip of segmented frames (not grouped by view).
              // Use the returned overlay image if present; fall back to raw image.
              if (examModeTab === 'blindSweep' && blindIsRecording) {
                // If we already rendered/pushed a drawn overlay this tick, don't also push the base frame.
                if (pushedBlindFrame) {
                  // no-op
                } else if (blindIsPausedRef.current) {
                  // no-op while paused
                } else {
                const frameImg = overlayImgSrc;
                if (typeof frameImg === 'string' && frameImg.length > 0) {
                  const recordingSweepId = recordingSweepIdRef.current;
                  const frameViews: string[] =
                    (typeof data !== 'string' &&
                      (((data as any)?.frames?.['0']?.views as any) ??
                        ((data as any)?.views as any))) ||
                    [];
                const frameQualityByView =
                  (typeof data !== 'string' &&
                    (((data as any)?.frames?.['0']?.quality_by_view as any) ??
                      ((data as any)?.frames?.['0']?.qualityByView as any) ??
                      ((data as any)?.frames?.['0']?.quality_criteria_by_view as any) ??
                      ((data as any)?.frames?.['0']?.qualityCriteriaByView as any))) ||
                  null;
                  if (recordingSweepId) {
                    setBlindSweeps((prev) =>
                      prev.map((s) => {
                        if (s.id !== recordingSweepId) return s;
                        const nextFrames: BlindSweepFrame[] = [
                        {
                          src: frameImg,
                          correlationId,
                          views: Array.isArray(frameViews) ? frameViews : [],
                          qualityByView:
                            frameQualityByView && typeof frameQualityByView === 'object'
                              ? (frameQualityByView as any)
                              : null,
                        },
                          ...((s.segmentedFrames as any) ?? []),
                        ].slice(0, 60);
                        return { ...s, segmentedFrames: nextFrames };
                      })
                    );
                    if (blindAutoFollowLatest) {
                      const next: BlindSweepFrame = {
                        src: frameImg,
                        correlationId,
                        views: Array.isArray(frameViews) ? frameViews : [],
                      qualityByView:
                        frameQualityByView && typeof frameQualityByView === 'object'
                          ? (frameQualityByView as any)
                          : null,
                      };
                      setBlindSelectedFrame(frameImg);
                      setBlindSelectedFrameInfo(next);
                    }
                  }
                }
                }
              }


              // Ensure the realtime global processor always receives a stable shape:
              // { image: string, frames: object, correlation_id: string, views?: [] }
              const stableReceived =
                typeof data === 'string'
                  ? {
                      image: data,
                      frames: {},
                      views: [],
                      correlation_id: correlationId,
                    }
                  : {
                      ...(data ?? {}),
                      image:
                        (data as any)?.image ??
                        (parsedMessage as any)?.image ??
                        baseImgSrc ??
                        null,
                      frames: (data as any)?.frames ?? {},
                      correlation_id: correlationId,
                    };

              const nextViews =
                typeof data === 'string' ? [] : ((data as any)?.views ?? []);

              setGlobalState((prevState) => ({
                ...prevState,
                receivedData: stableReceived as any,
                viewList: [...new Set([...prevState.viewList, ...nextViews])],
              }));
            },
            [modalCanvasVideoRef]
          );

          setIsInferencing(true);
          interval123 = setInterval(() => {
            if (Date.now() - lastFrameTime > frameTimeout) {
              console.log('Stream stopped or no data received for 5 seconds.');
            }
          }, 1000);
        }
      } else {
        // Guard: wsRef can be null during teardown or reconnects
        globalState?.wsRef?.current?.setOnFrameCallback?.(null);
        if (processedStreamRef.current) {
          processedStreamRef.current
            .getTracks()
            .forEach((track) => track.stop());
          processedStreamRef.current = null;
        }

        if (dataChannelRef.current) {
          dataChannelRef.current.close();
        }
        if (pcRef.current) {
          pcRef.current.close();
          pcRef.current = null;
        }
        if (videoStream && videoRef.current) {
          console.log('Setting video source object :');
          videoRef.current.srcObject = videoStream;
          // Avoid noisy AbortError if the source is swapped quickly
          void videoRef.current.play().catch(() => undefined);
        }
        setIsInferencing(false);

        if (intervalId) clearInterval(intervalId as any);
      }
    };

    setupWebRTC();

    return () => {
      if (processedStreamRef.current) {
        processedStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (pcRef.current) {
        pcRef.current.close();
        pcRef.current = null;
      }
      if (intervalId) clearInterval(intervalId);
      if (captureIntervalRef.current) clearInterval(captureIntervalRef.current as any);
      if (interval123) {
        clearInterval(interval123);
      }
    };
  }, [
    globalState.isVideoFrozen,
    globalState?.isStart,
    videoStream,
    bool,
    globalState?.machineType,
    examModeTab,
    blindIsRecording,
    blindIsPaused,
    examTypeChosen,
  ]);

  useEffect(() => {
    if (videoStream && blindSweepVideoRef.current) {
      blindSweepVideoRef.current.srcObject = videoStream;
    }
  }, [videoStream]);

  useEffect(() => {
    // Ensure the offscreen processing video always has the stream attached
    // so `captureFrame()` can read `videoWidth/videoHeight` reliably.
    if (videoStream && videoRef.current) {
      try {
        videoRef.current.srcObject = videoStream;
        void videoRef.current.play().catch(() => undefined);
      } catch {}
    }
  }, [videoStream]);

  // Blind Sweep capture loop is started/stopped explicitly by:
  // startBlindSweepRecording / pauseBlindRecording / resumeBlindRecording / stopBlindRecording

  useEffect(() => {
    // When switching into Blind Sweep, ensure any previous Realtime capture interval is cleared
    // so Blind Sweep can take over capture timing cleanly.
    if (examModeTab !== 'blindSweep') return;
    try {
      if (intervalId) clearInterval(intervalId as any);
    } catch {}
    try {
      if (captureIntervalRef.current) clearInterval(captureIntervalRef.current as any);
    } catch {}
    setIntervalId(null);
    captureIntervalRef.current = null;
  }, [examModeTab]);

  useEffect(() => {
    return () => {
      croppedStream?.getTracks().forEach((track) => track.stop());
    };
  }, [croppedStream]);

  const API = process.env.BACKEND_URL?.replace(/\/api\/v1$/, '');

  useEffect(() => {
    return () => {
      stopVideoStream();
    };
  }, [globalState?.isStart, bool]);

  const mediaTypeSwitcher = () => {
    mediaTypeMap.forEach((mediaType, option) => {
      if (globalState.optionValueDevice === option) {
        setDeviceMediaType(mediaType);
      }
    });
  };

  useEffect(() => {
    if (isInferencing) {
      setIsVideoLoading(true);
      const timeout = setTimeout(() => setIsVideoLoading(false), 1200);
      return () => clearTimeout(timeout);
    } else {
      setIsVideoLoading(false);
    }
  }, [isInferencing]);

  const handleChange = (e) => {
    handleVideoClick();
  };

  const firstDetectedPlane = globalState?.lastDetectedPlane
    ?.split(',')?.[0]
    ?.trim();

  const predictionHistory =
    globalState?.planes[firstDetectedPlane]?.predictionHistory?.slice(0, 4) ||
    [];

  const skeletonCount = Math.max(4 - predictionHistory.length, 0);
  const [divHeight, setDivHeight] = useState('60%');

  const calculateHeight = () => {
    const availableHeight = window.innerHeight;
    const calculatedHeight = availableHeight;
    setDivHeight(`${calculatedHeight}px`);
  };

  useEffect(() => {
    // setGlobalState((prevState) => ({
    //     ...prevState,
    //     doctorName: user?.profile?.name,
    // }));
    calculateHeight();
    window.addEventListener('resize', calculateHeight);

    return () => {
      window.removeEventListener('resize', calculateHeight);
    };
  }, []);

  const countPlanesWithFrame = (planes) => {
    let count = 0;
    for (const planeKey in planes) {
      if (planes[planeKey]?.frame) {
        count++;
      }
    }
    return count;
  };

  const planesWithFrameCount = countPlanesWithFrame(globalState.planes);
  const isButtonDisabled =
    !Object.keys(globalState?.planes).some(
      (tag) => globalState?.planes[tag]?.selectedViewData
    ) || globalState?.patient?.patient_name == null;

  const handlePauseResume = () => {
    setIsChecked(!isChecked);
    setGlobalState((prevState) => ({
      ...prevState,
      isVideoFrozen: !prevState.isVideoFrozen,
    }));
  };

  const handleVideoClick = () => {
    setIsChecked(!isChecked);
    setGlobalState((prevState) => ({
      ...prevState,
      isVideoFrozen: !prevState.isVideoFrozen,
    }));
  };

  const [examDuration, setExamDuration] = useState(realtimeExamDuration);
  const [isTimerRunning, setIsTimerRunning] = useState(false);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;

    const isSetupComplete = globalState.deviceId && globalState?.machineConfig;

    if (
      isSetupComplete &&
      !globalState.isVideoFrozen &&
      !globalState.isExamEnded &&
      examDuration > 0
    ) {
      setIsTimerRunning(true);
      timer = setInterval(() => {
        setExamDuration((prev) => {
          if (prev <= 1) {
            setGlobalState((prevState) => ({
              ...prevState,
              isExamEnded: true,
              isVideoFrozen: true,
            }));
            setIsTimerRunning(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      setIsTimerRunning(false);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [
    globalState.isVideoFrozen,
    globalState.isExamEnded,
    examDuration,
    globalState.deviceId,
    globalState.selectedItem,
  ]);

  const calibratePlanesMeasurements = (planes: Record<string, any>, pixelSpacingMap: Record<string, any>) => {
    const calibratedPlanes: Record<string, any> = {};
    
    Object.entries(planes).forEach(([viewName, viewData]: [string, any]) => {
      if (!viewData?.predictionHistory) {
        calibratedPlanes[viewName] = viewData;
        return;
      }

      const calibratedHistory = viewData.predictionHistory.map((frame: any) => {
        if (!frame.correlation_id || !frame.measurements) return frame;

        if (frame.measurements.unit === 'cm') return frame;

        const pixelSpacingData = pixelSpacingMap?.[frame.correlation_id];
        const estimatedMmPerPixel = pixelSpacingData?.estimated_mm_per_pixel;

        if (estimatedMmPerPixel === null || estimatedMmPerPixel === undefined) return frame;

        const calibratedMeasurements: Record<string, number | string> = { unit: 'cm' };
        
        Object.entries(frame.measurements).forEach(([key, value]) => {
          if (key === 'unit') return;
          const valueInPx = Number(value);
          const valueInMm = valueInPx * estimatedMmPerPixel;
          const valueInCm = valueInMm / 10;
          calibratedMeasurements[key] = valueInCm;
        });

        return {
          ...frame,
          measurements: calibratedMeasurements,
        };
      });

      calibratedPlanes[viewName] = {
        ...viewData,
        predictionHistory: calibratedHistory,
      };
    });

    return calibratedPlanes;
  };

  const sendAllPredictionsToBackend = async (planes, patient, pregnancy) => {
    try {
      const sessionId = globalState.sessionId;
      const isFromWorklist = globalState.isFromWorklist;

      const calibratedPlanes = calibratePlanesMeasurements(planes, globalState.pixelSpacingMap);

      const pixelSpacingMap = globalState.pixelSpacingMap || {};

      const studyType =
        pendingExamEndModeRef.current === 'blindSweep' ? 'BLIND' : 'REALTIME';

      const payload: Record<string, unknown> = {
        session_id: sessionId,
        // Enhancement: tag the created DICOM/report by study type.
        // Backend can use this to distinguish blind sweep vs realtime.
        study_type: studyType,
        type: studyType,
        views: Object.entries(calibratedPlanes).map(
          ([viewName, viewData]: [string, any]) => ({
            view: viewName,
            prediction_history: viewData.predictionHistory.map(
              ({
                frameIndex,
                measurements,
                quality_criteria,
                score,
                structuresToDraw,
                imageRef,
                selectedForReport,
                correlation_id,
              }: any) => {
                const spacingData = correlation_id
                  ? pixelSpacingMap[correlation_id]
                  : null;
                const resolvedPixelSpacing =
                  spacingData?.estimated_mm_per_pixel ?? null;

                return {
                  frameIndex,
                  imageRef,
                  measurements,
                  pixel_spacing: resolvedPixelSpacing,
                  quality_criteria,
                  score,
                  structuresToDraw,
                  selectedForReport,
                };
              }
            ),
          })
        ),
      };

      const isFromLocalForm =
        globalState.isFromLocalForm || pregnancy?.isFromLocalForm;

      if (isFromWorklist && patient?.mwl) {
        const mwl = patient.mwl;
        payload.patient = {
          PatientName: mwl.PatientName || '',
          PatientID: mwl.PatientID || '',
          PatientBirthDate: mwl.PatientBirthDate || '',
          PatientSex: mwl.PatientSex || '',
        };
        if (mwl.StudyInstanceUID) {
          payload.study_instance_uid = mwl.StudyInstanceUID;
        }
      } else if (patient?.id) {
        payload.patient_id = patient.id;
      } else {
        payload.patient = patient;
      }

      if (pregnancy?.existsInBackend && pregnancy?.id) {
        payload.pregnancy_id = pregnancy.id;
      } else if (
        pregnancy?.id &&
        (pregnancy?.isFromLocalForm || isFromLocalForm)
      ) {
        payload.pregnancy_id = pregnancy.id;
      } else if (pregnancy?.id && !pregnancy?.isFromWorklist) {
        payload.pregnancy_id = pregnancy.id;
      } else if (pregnancy?.isFromWorklist || isFromWorklist) {
        payload.pregnancy = {
          lmp: pregnancy.lmp || null,
          edd: pregnancy.edd || null,
          ultrasoundDate: pregnancy.ultrasoundDate || null,
          crl: pregnancy.crl || pregnancy.preg_crl || null,
          bpd: pregnancy.bpd || pregnancy.preg_bpd || null,
          hc: pregnancy.hc || pregnancy.preg_hc || null,
          ac: pregnancy.ac || pregnancy.preg_ac || null,
          fl: pregnancy.fl || pregnancy.preg_fl || null,
        };
      } else if (pregnancy) {
        payload.pregnancy = pregnancy;
      }

      const response = await saveViews(payload);
    } catch (error) {
      console.error('[sendAllPredictionsToBackend] API error:', error);
    }
  };

  const buildPlanesFromBlindSweeps = useCallback(() => {
    // Build a minimal `planes` structure compatible with `sendAllPredictionsToBackend`
    // using Blind Sweep segmented frames metadata.
    const planes: Record<string, { predictionHistory: any[] }> = {};
    for (const sweep of blindSweeps) {
      const frames = (sweep.segmentedFrames ?? []) as BlindSweepFrame[];
      for (const frame of frames) {
        const views = Array.isArray(frame.views) ? frame.views : [];
        for (const viewName of views) {
          if (!viewName) continue;
          const history = (planes[viewName]?.predictionHistory ?? []) as any[];
          // Keep a small number of frames per view (enough to create DICOM/views)
          if (history.length >= 8) continue;

          const quality_criteria =
            frame.qualityByView && typeof frame.qualityByView === 'object'
              ? (frame.qualityByView as any)?.[viewName] ?? null
              : null;

          const nextEntry = {
            frameIndex: history.length,
            imageRef: frame.src, // already the segmented/overlay image dataURL
            measurements: null,
            quality_criteria,
            score: null,
            structuresToDraw: [],
            selectedForReport: true,
            correlation_id: frame.correlationId ?? null,
          };

          planes[viewName] = { predictionHistory: [...history, nextEntry] };
        }
      }
    }
    return planes;
  }, [blindSweeps]);

  useEffect(() => {
    const processPlanes = async () => {
      try {
        const updatedPlanes = { ...globalState.planes };

        setGlobalState((prevState) => ({
          ...prevState,
          planes: updatedPlanes,
        }));

        // Only save if patient and pregnancy are selected
        if (globalState?.patient && globalState?.pregnancy) {
          const result = await sendAllPredictionsToBackend(
            globalState?.planes,
            globalState?.patient,
            globalState?.pregnancy
          );
        }
      } catch (error) {
        console.error('[processPlanes] Error in processing planes:', error);
      }
    };

    if (globalState.isExamEnded) {
      processPlanes();
    }
  }, [globalState.isExamEnded]);

  function updateGlobalStateWithFrameIds(currentState, response) {
    const updatedPlanes = Object.keys(currentState.planes).reduce(
      (acc, viewKey) => {
        if (
          response[viewKey] &&
          currentState.planes[viewKey].predictionHistory
        ) {
          const frameIdMap = response[viewKey].reduce((map, frameInfo) => {
            map[frameInfo.original_frame_number] = frameInfo.frame_id;
            return map;
          }, {});

          const newPredictionHistory = currentState.planes[
            viewKey
          ].predictionHistory.map((prediction) => ({
            ...prediction,
            frame_id: frameIdMap[prediction.frameIndex] || null,
          }));

          acc[viewKey] = {
            ...currentState.planes[viewKey],
            predictionHistory: newPredictionHistory,
          };
        } else {
          acc[viewKey] = currentState.planes[viewKey];
        }
        return acc;
      },
      {}
    );

    setGlobalState((prevState) => ({
      ...prevState,
      planes: updatedPlanes,
    }));

    return {
      ...currentState,
      planes: updatedPlanes,
    };
  }

  // Clean states on page load (mount)
  useEffect(() => {
    cleanupBlobUrls(globalState.planes);

    setGlobalState((prevState) => ({
      ...prevState,
      isExamEnded: false,
      isVideoFrozen: true,
      lastDetectedPlane: '',
      planes: {},
      pixelSpacingMap: {},
      patient: null,
      viewList: [],
      viewDetectionOrder: [],
      reportModalOpened: false,
      receivedData: null,
      deviceId: '',
      selectedItem: 'Select your machine',
      isStart: false,
      inputDevices: [],
      selectedItemsMultidrop: [],
      optionValueDevice: 'USB',
    }));
    setBool(true);
    setVideoMaskStream && setVideoMaskStream(null);
    setIsChecked(false);
    setSelectedPlansModal('');
    setSelectedPlansTag('');
    setShowDeviceModal(true);
    setIsInitialSetupComplete(false);
    setIsVideoBigDisplayOpen(false);
    setShowPatientModal(false);
    setSelectedRow(null);
    setExamDuration(realtimeExamDuration);
    setIsTimerRunning(false);
    setSavedStudyInstanceUID(null);
  }, []);

  useEffect(() => {
    if (sessionEnded) {
      // Stop video stream after successful completion
      if (videoStream) {
        videoStream.getTracks().forEach((track) => track.stop());
      }

      // Reset sessionEnded flag but keep isExamEnded true so the post-exam buttons show
      setSessionEnded(false);
    }
  }, [sessionEnded, videoStream]);

  useEffect(() => {
    return () => {
      cleanupBlobUrls(globalState.planes);
    };
  }, []);

  const isInitialState = !globalState.deviceId && !globalState?.machineConfig;
  const isSessionActive = globalState.deviceId && globalState?.machineConfig;

  // Fetch devices and machines when exam is paused
  useEffect(() => {
    const fetchDevicesAndMachines = async () => {
      if (
        isSessionActive &&
        globalState.isVideoFrozen &&
        !globalState.isExamEnded
      ) {
        // Enumerate video devices
        try {
          if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
            const stream = await navigator.mediaDevices.getUserMedia({
              video: true,
              audio: true,
            });
            stream.getTracks().forEach((track) => track.stop());

            const devices = await navigator.mediaDevices.enumerateDevices();
            const videoDevices = devices.filter((device) => {
              const label = device.label.toLowerCase();
              // Exclude camera devices - keep capture cards and other video input devices
              const isCameraDevice =
                label.includes('camera') ||
                label.includes('webcam') ||
                label.includes('facetime') ||
                label.includes('integrated') ||
                label.includes('built-in') ||
                label.includes('front') ||
                label.includes('back') ||
                label.includes('selfie') ||
                label.includes('iphone') ||
                label.includes('ipad') ||
                label.includes('android');

              return device.kind === 'videoinput' && !isCameraDevice;
            });
            setAvailableFeedSources(videoDevices);
          }
        } catch (error) {
          console.error('Failed to enumerate devices:', error);
        }

        // Fetch machines
        try {
          const response = await apiClient.getUltrasoundMachines();
          setMachinesList(response.data || []);
          // Set current machine as selected
          if (globalState.machineConfig) {
            setSelectedMachineForChange(
              globalState.machineConfig.id.toString()
            );
          }
        } catch (error) {
          console.error('Failed to fetch machines:', error);
        }

        // Set current feed type and source
        setSelectedFeedType(globalState.optionValueDevice || 'USB');
        if (globalState.deviceId) {
          setSelectedFeedSource(globalState.deviceId);
        } else if (availableFeedSources.length > 0) {
          setSelectedFeedSource(availableFeedSources[0].deviceId);
        }
      }
    };

    fetchDevicesAndMachines();
  }, [isSessionActive, globalState.isVideoFrozen, globalState.isExamEnded]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target as HTMLElement;
      if (
        !target.closest('.feed-dropdown-container') &&
        !target.closest('.machine-dropdown-container')
      ) {
        setShowFeedDropdown(false);
        setShowMachineDropdown(false);
      }
    };

    if (showFeedDropdown || showMachineDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showFeedDropdown, showMachineDropdown]);

  const setDeviceIdCallback = useCallback(
    (value) => {
      setGlobalState((prevState) => ({
        ...prevState,
        deviceId: value,
      }));
    },
    [setGlobalState]
  );

  const setInputDevicesCallback = useCallback(
    (value) => {
      setGlobalState((prevState) => ({
        ...prevState,
        inputDevices: value,
      }));
    },
    [setGlobalState]
  );

  // Wizard state for Step2_PatientAndPregnancy component
  const [wizardState, setWizardState] = useState({
    patient: null,
    pregnancy: null,
    extractedPatients: [],
  });

  // Hydrate patient/pregnancy from appointment id (scheduling handoff).
  useEffect(() => {
    const appointmentId = searchParams.get('appointmentId');
    if (!appointmentId) return;

    let cancelled = false;

    (async () => {
      try {
        const appointmentRes: any = await apiClient.get(
          `/appointments/${appointmentId}`
        );
        const appointment =
          appointmentRes?.data ?? appointmentRes ?? null;
        const patientId = appointment?.patient_id;
        if (!patientId) {
          throw new Error('Appointment has no patient_id');
        }

        const pregnancyId = appointment?.pregnancy_id ?? null;
        const accession = appointment?.accession_number ?? null;

        const response: any = await apiClient.get(
          `patients/${patientId}/dossier`
        );
        const body = response?.data ?? response;
        const patient = body?.patient ?? body;
        const dossierPregnancy = body?.pregnancy ?? patient?.pregnancy ?? null;
        const pregnancies: any[] = [
          ...(dossierPregnancy ? [dossierPregnancy] : []),
          ...(Array.isArray(body?.pregnancies) ? body.pregnancies : []),
          ...(Array.isArray(patient?.pregnancies) ? patient.pregnancies : []),
        ];
        let pregnancy =
          (pregnancyId &&
            pregnancies.find((p) => String(p.id) === String(pregnancyId))) ||
          pregnancies.find(
            (p) =>
              String(p.pregnancy_status || p.status || '').toLowerCase() ===
              'active'
          ) ||
          pregnancies[0] ||
          null;

        if (cancelled) return;

        setGlobalState((prev) => ({
          ...prev,
          patient,
          pregnancy,
          appointmentId: appointmentId || prev.appointmentId || null,
          accessionNumber: accession || prev.accessionNumber || null,
        }));
        setWizardState({
          patient,
          pregnancy,
          extractedPatients: [],
        });
      } catch (err) {
        console.error('Failed to hydrate exam patient from scheduling', err);
        messageI18n.error('realtime.selectPatientAndPregnancy');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams, setGlobalState]);

  // Handler for End Session - show patient/pregnancy modal
  const handleEndSession = () => {
    pendingExamEndModeRef.current = 'realtime';
    // Check if there are any views captured
    const hasViews = Object.keys(globalState.planes).some(
      (tag) => globalState?.planes[tag]?.predictionHistory?.length > 0
    );

    if (!hasViews) {
      messageI18n.warning('realtime.noViewsBeforeEndSession');
      return;
    }

    // Pause the video and stop inferencing (but keep video stream active for potential resume)
    setGlobalState((prevState) => ({
      ...prevState,
      isVideoFrozen: true,
      isStart: false,
    }));

    setIsTimerRunning(false);
    setExamDuration(realtimeExamDuration);

    // Reset wizard state with current patient/pregnancy or null
    setWizardState({
      patient: globalState.patient || null,
      pregnancy: globalState.pregnancy || null,
      extractedPatients: [],
    });

    // Show the patient/pregnancy modal
    setShowPatientPregnancyModal(true);
  };

  const handleFinishBlindSweepExam = useCallback(() => {
    pendingExamEndModeRef.current = 'blindSweep';

    // Pre-fill wizard with current selections (same UX as realtime)
    setWizardState({
      patient: globalState.patient || null,
      pregnancy: globalState.pregnancy || null,
      extractedPatients: [],
    });

    setShowPatientPregnancyModal(true);
  }, [globalState.patient, globalState.pregnancy]);

  // ── Blind sweep: at EXAM END select patient then create study + upload all sweeps ─────────
  const handleConfirmPatientForBlindSweep = useCallback(async () => {
    setShowBlindPatientModal(false);
    try {
      const res = await createBlindStudyAsync({
        patient_id: (wizardState as any)?.patient?.id ?? (wizardState as any)?.worklistItem?.id ?? null,
        pregnancy_id: (wizardState as any)?.pregnancy?.id ?? (wizardState as any)?.worklistPregnancy?.id ?? null,
        study_mode: 'RECORDER',
        type: 'BLIND',
      });
      const createdUid =
        res?.data?.result?.study_instance_uid ??
        res?.data?.result?.study?.study_instance_uid ??
        res?.data?.result?.created_study?.study_instance_uid ??
        res?.data?.result?.study_instance_uid ??
        null;
      if (!createdUid) throw new Error('Missing study_instance_uid');
      setBlindStudyUID(String(createdUid));
      const uid = String(createdUid);
      messageI18n.success('realtime.blindSweepExamCreated');

      // Upload the 7 recorded sweeps now.
      for (const sweep of blindSweeps) {
        if (!sweep?.blob) continue;
        const sweepId = sweep.id;
        setBlindSweeps((prev) => prev.map((s) => (s.id === sweepId ? { ...s, state: 'uploading', error: null } : s)));
        const file = new File([sweep.blob], `sweep_${sweepId}.webm`, { type: 'video/webm' });
        const formData = new FormData();
        formData.append('file', file);
        formData.append('instance', JSON.stringify({
          study_instance_uid: uid,
          sweep_id: String(sweepId),
          patient_id: (wizardState as any)?.patient?.id ?? 1,
        }));
        await createBlindInstanceAsync({ study_instance_uid: uid, index: sweepId, pixel_spacing: 1, body: formData });
        setBlindSweeps((prev) => prev.map((s) => (s.id === sweepId ? { ...s, state: 'done' } : s)));
      }
    } catch {
      messageI18n.error('realtime.blindSweepExamCreateFailed');
      setShowBlindPatientModal(true);
    }
  }, [createBlindStudyAsync, createBlindInstanceAsync, blindSweeps, wizardState]);

  const stopBlindRecording = useCallback(() => {
    try { mediaRecorderRef.current?.stop(); } catch {}
    setBlindIsPaused(false);
    // Stop sending frames immediately (don't wait for MediaRecorder.onstop)
    setBlindIsRecording(false);
    console.log('[BlindSweep] stop recording (mute WS + stop capture)');
    if (blindCountdownRef.current) {
      clearInterval(blindCountdownRef.current);
      blindCountdownRef.current = null;
    }
    recordingSweepIdRef.current = null;
    blindWsAcceptRef.current = false;
    blindFpsRef.current.t0 = 0;
    blindFpsRef.current.frames = 0;
    blindFpsRef.current.lastLog = 0;
    blindCaptureTickRef.current.ticks = 0;
    blindCaptureTickRef.current.lastLog = 0;
    // Best-effort: restore track framerate after blind recording stops
    try {
      const track = videoStream?.getVideoTracks?.()?.[0];
      const original = blindOriginalTrackFrameRateRef.current;
      if (track && original && typeof track.applyConstraints === 'function') {
        void track
          .applyConstraints({ frameRate: { ideal: original, max: original } } as any)
          .catch(() => undefined);
      }
    } catch {}
    try {
      globalState?.wsRef?.current?.setOnFrameCallback(null);
    } catch {}
  }, [videoStream]);

  const startBlindSweepRecording = useCallback(async (sweepId: number) => {
    if (!videoStream) {
      messageI18n.error('realtime.streamNotReady');
      return;
    }
    // WS connection is managed by the global provider (same as realtime).
    // Ensure the realtime session is actively inferencing during the 10s sweep.
    // (Blind Sweep inference is intentionally limited to the recording window.)
    if (!globalState.isStart || globalState.isVideoFrozen) {
      setGlobalState((prevState) => ({
        ...prevState,
        isStart: true,
        isVideoFrozen: false,
      }));
    }
    recordingChunksRef.current = [];
    recordingSweepIdRef.current = sweepId;
    blindWsAcceptRef.current = true;
    setBlindIsRecording(true);
    setBlindIsPaused(false);
    // Ensure processing <video> is ready BEFORE starting capture.
    await ensureBlindProcessingVideoReady();
    // Start capture immediately for this sweep
    // Prime one frame immediately (helps with first metadata load / WS connect)
    try { captureFrame(); } catch {}
    // reset FPS window for this sweep
    blindFpsRef.current.t0 = 0;
    blindFpsRef.current.frames = 0;
    blindFpsRef.current.lastLog = 0;
    blindCaptureTickRef.current.ticks = 0;
    blindCaptureTickRef.current.lastLog = 0;
    blindRecordingStartPerfRef.current = performance.now();
    // Blind Sweep: cap recorded video to ~20fps (best-effort).
    // This affects the underlying stream track, which is fine because Blind Sweep locks the workflow.
    try {
      const track = videoStream.getVideoTracks?.()?.[0];
      const settings = track?.getSettings?.() as any;
      if (blindOriginalTrackFrameRateRef.current == null) {
        blindOriginalTrackFrameRateRef.current =
          typeof settings?.frameRate === 'number' && settings.frameRate > 0
            ? settings.frameRate
            : 30;
      }
      if (track && typeof track.applyConstraints === 'function') {
        await track.applyConstraints({ frameRate: { ideal: 20, max: 20 } } as any);
        const after = track.getSettings?.() as any;
        console.log(
          `[BlindSweep] applied 20fps constraint: before=${settings?.frameRate ?? 'n/a'} after=${after?.frameRate ?? 'n/a'}`
        );
      }
    } catch (e) {
      console.warn('[BlindSweep] failed to apply 20fps constraint:', e);
    }
    try {
      const track = videoStream.getVideoTracks?.()[0];
      const settings = track?.getSettings?.() as any;
      console.log(
        `[BlindSweep] stream settings: frameRate=${settings?.frameRate ?? 'n/a'} size=${settings?.width ?? 'n/a'}x${settings?.height ?? 'n/a'}`
      );
    } catch {}
    console.log(`[BlindSweep] start recording sweep ${sweepId}`);
    setBlindSelectedFrame(null);
    setBlindAutoFollowLatest(true);
    setBlindSweeps((prev) =>
      prev.map((s) =>
        s.id === sweepId
          ? ({
              ...s,
              state: 'recording',
              secondsLeft: BLIND_SECONDS,
              error: null,
              segmentedFrames: [],
              latestSegPayload: null,
            } as any)
          : s
      )
    );

    // Improve recording quality (higher bitrate + prefer VP9 when available).
    const preferredMimeTypes = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
    ];
    const mimeType =
      preferredMimeTypes.find((t) => (window as any)?.MediaRecorder?.isTypeSupported?.(t)) ??
      'video/webm';
    const mr = new MediaRecorder(videoStream, {
      mimeType,
      videoBitsPerSecond: 8_000_000,
      audioBitsPerSecond: 128_000,
    } as any);
    mediaRecorderRef.current = mr;
    mr.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) recordingChunksRef.current.push(e.data);
    };
    mr.onstop = async () => {
      const blob = new Blob(recordingChunksRef.current, { type: 'video/webm' });
      const blobUrl = URL.createObjectURL(blob);
      setBlindIsRecording(false);
      recordingSweepIdRef.current = null;
      blindWsAcceptRef.current = false;
      // Log an effective "recorded fps" based on captured frames during this sweep.
      try {
        const durSec = Math.max(
          0.001,
          (performance.now() - (blindRecordingStartPerfRef.current || performance.now())) / 1000
        );
        const frames = blindFpsRef.current.frames || 0;
        const effFps = frames / durSec;
        console.log(
          `[BlindSweep] sweep ${sweepId} recorded: capturedFrames=${frames} duration=${durSec.toFixed(2)}s effectiveFPS=${effFps.toFixed(2)}`
        );
        if (frames === 0) {
          console.warn(
            '[BlindSweep] No captured frames counted during recording. This means captureFrame() did not run (or videoWidth/videoHeight stayed 0).'
          );
        }
      } catch {}
      setBlindSweeps((prev) => prev.map((s) => s.id === sweepId ? ({ ...s, blobUrl, blob, state: 'done', secondsLeft: 0 }) : s));
      messageI18n.success('realtime.sweepRecorded', { sweepId });
      if (sweepId < BLIND_NUM_SWEEPS) setBlindActiveSweep(sweepId + 1);
    };

    mr.start(250);
    if (blindCountdownRef.current) clearInterval(blindCountdownRef.current);
    let left = BLIND_SECONDS;
    blindCountdownRef.current = setInterval(() => {
      if (blindIsPausedRef.current) return;
      left -= 1;
      setBlindSweeps((prev) => prev.map((s) => s.id === sweepId ? ({ ...s, secondsLeft: Math.max(0, left) }) : s));
      if (left <= 0) {
        if (blindCountdownRef.current) clearInterval(blindCountdownRef.current);
        stopBlindRecording();
      }
    }, 1000);
  }, [
    videoStream,
    globalState.isStart,
    globalState.isVideoFrozen,
    setGlobalState,
    stopBlindRecording,
    captureFrame,
    ensureBlindProcessingVideoReady,
  ]);

  const pauseBlindRecording = useCallback(() => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.pause();
      }
    } catch {}
    // Keep the ref in sync immediately (avoid 1-render lag).
    blindIsPausedRef.current = true;
    // Match Realtime semantics: freeze the exam (stops inference/capture cleanly).
    setGlobalState((prev) => ({ ...prev, isVideoFrozen: true }));
    // Blind Sweep pause must stop sending frames AND ignore any incoming WS frames.
    // We don't disconnect the WS because it's shared with Realtime.
    blindWsAcceptRef.current = false;
    console.log('[BlindSweep] pause (mute WS + stop capture)');
    try {
      const svc: any = globalState?.wsRef?.current;
      console.log('[BlindSweep] PAUSE snapshot', {
        wsReadyState: svc?.socket?.readyState,
        wsConnected: !!globalState?.wsRef?.current?.isConnected?.(),
        isVideoFrozen: true,
      });
    } catch {}
    setBlindIsPaused(true);
  }, [setGlobalState]);

  const resumeBlindRecording = useCallback(async () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
        mediaRecorderRef.current.resume();
      }
    } catch {}
    // Keep the ref in sync immediately (avoid 1-render lag).
    blindIsPausedRef.current = false;
    // Match Realtime semantics: unfreeze the exam (allows inference/capture to run).
    setGlobalState((prev) => ({ ...prev, isStart: true, isVideoFrozen: false }));
    // Resume receiving/processing only while recording.
    if (blindIsRecording || recordingSweepIdRef.current) blindWsAcceptRef.current = true;
    // Ensure processing <video> is ready again (metadata can drop to 0 after pause)
    await ensureBlindProcessingVideoReady();
    // WS connection is managed by the global provider (same as realtime).
    setBlindIsPaused(false);
    if (blindIsRecording || recordingSweepIdRef.current) {
      // Prime one frame immediately (now that metadata is ready)
      try { captureFrame(); } catch {}
      console.log('[BlindSweep] resume (unmute WS; capture resumes)');
      try {
        const svc: any = globalState?.wsRef?.current;
        console.log('[BlindSweep] RESUME snapshot', {
          wsReadyState: svc?.socket?.readyState,
          wsConnected: !!globalState?.wsRef?.current?.isConnected?.(),
          isVideoFrozen: globalState?.isVideoFrozen,
          accept: blindWsAcceptRef.current,
        });
      } catch {}
      setTimeout(() => {
        console.log('[BlindSweep] after resume: captureIntervalRef?', !!captureIntervalRef.current);
      }, 400);
    }
  }, [
    captureFrame,
    globalState?.wsRef,
    ensureBlindProcessingVideoReady,
    blindIsRecording,
    setGlobalState,
  ]);

  // Handler for confirming patient/pregnancy and ending the exam
  const handleConfirmPatientAndEndExam = async () => {
    console.log('[handleConfirmPatientAndEndExam] wizardState:', wizardState);

    const hasLocalPatient = wizardState.patient && wizardState.pregnancy;
    const hasWorklistPatient =
      wizardState.worklistItem && wizardState.worklistPregnancy;

    if (!hasLocalPatient && !hasWorklistPatient) {
      messageI18n.error('realtime.selectPatientAndPregnancy');
      return;
    }

    setShowPatientPregnancyModal(false);

    const isBlind = pendingExamEndModeRef.current === 'blindSweep';
    const blindPlanes = isBlind ? buildPlanesFromBlindSweeps() : null;

    if (hasWorklistPatient) {
      setGlobalState((prevState) => ({
        ...prevState,
        ...(isBlind ? { planes: blindPlanes ?? prevState.planes } : null),
        patient: (wizardState as any).worklistItem,
        pregnancy: (wizardState as any).worklistPregnancy,
        isFromWorklist: true,
        isExamEnded: true,
      }));
    } else {
      setGlobalState((prevState) => ({
        ...prevState,
        ...(isBlind ? { planes: blindPlanes ?? prevState.planes } : null),
        patient: wizardState.patient,
        pregnancy: wizardState.pregnancy,
        isFromWorklist: false,
        isExamEnded: true,
      }));
    }
  };

  const handlePatientPregnancyClose = () => {
    setShowPatientPregnancyModal(false);

    setGlobalState((prevState) => ({
      ...prevState,
      isVideoFrozen: true,
      isStart: false,
    }));
  };

  const handleDirectSaveFromPregnancyModal = async (
    patientOrWorklistItem: any,
    pregnancyData: any
  ) => {
    if (!patientOrWorklistItem) {
      messageI18n.error('realtime.noPatientOrWorklist');
      return;
    }

    setShowPatientPregnancyModal(false);

    const isFromLocalForm =
      pregnancyData?.isFromLocalForm ||
      (patientOrWorklistItem?.id && !patientOrWorklistItem?.mwl);
    const isFromWorklist = !isFromLocalForm && patientOrWorklistItem?.mwl;

    const pregnancyPayload: Record<string, any> = {
      lmp: pregnancyData?.lmp || null,
      edd: pregnancyData?.edd || null,
      ultrasoundDate: null,
      crl: pregnancyData?.crl ? parseFloat(pregnancyData.crl) : null,
      bpd: pregnancyData?.bpd ? parseFloat(pregnancyData.bpd) : null,
      hc: pregnancyData?.hc ? parseFloat(pregnancyData.hc) : null,
      ac: pregnancyData?.ac ? parseFloat(pregnancyData.ac) : null,
      fl: pregnancyData?.fl ? parseFloat(pregnancyData.fl) : null,
      isFromWorklist: isFromWorklist,
      isFromLocalForm: isFromLocalForm,
    };

    if (pregnancyData?.existsInBackend && pregnancyData?.id) {
      pregnancyPayload.existsInBackend = true;
      pregnancyPayload.id = pregnancyData.id;
      pregnancyPayload.patient_id = pregnancyData.patient_id;
    }

    setGlobalState((prevState) => ({
      ...prevState,
      patient: patientOrWorklistItem,
      pregnancy: pregnancyPayload,
      isFromWorklist: isFromWorklist,
      isFromLocalForm: isFromLocalForm,
      isExamEnded: true,
    }));

    messageI18n.success('realtime.savingViews');
  };

  return (
    <>
      <div
        className={`relative mx-auto flex h-full w-full flex-col bg-slate-50 dark:bg-gray-900 font-sans`}
      >
        {/* Hidden canvas */}
        <canvas ref={processingCanvasRef} style={{ display: 'none' }}   />

        {/* Feed Selection Modal - Shows on page load */}
        <StepperFeedRealtime
          setShowDeviceModal={setShowDeviceModal}
          showDeviceModal={showDeviceModal && !showFeedPreviewModal}
          startVideoStream={startVideoStream}
          mediaTypeSwitcher={mediaTypeSwitcher}
          globalState={globalState}
          setGlobalState={setGlobalState}
          inputDevices={globalState.inputDevices}
          videoStream={videoStream}
          setVideoStream={setVideoStream}
          handleNext={() => {
            setShowDeviceModal(false);
            setIsInitialSetupComplete(true);
            setResetStepperOnOpen(false);
            setShowExamTypeChoice(true);
            // setGlobalState((prevState) => ({
            //     ...prevState,
            //     isVideoFrozen: false,
            //     isStart: true,
            // }));
          }}
          setDeviceId={setDeviceIdCallback}
          setInputDevices={setInputDevicesCallback}
          autoSelect={true}
          resetOnOpen={resetStepperOnOpen}
        />

        <SelectView
          selectedPlan={selectedPlansModal}
          setSelectedPlansModal={setSelectedPlansModal}
          tag={selectedPlansTag}
          globalState={globalState}
          setGlobalState={setGlobalState}
          studyUID={null}
        />

        {/* <ReportModal
          globalState={globalState}
          setGlobalState={setGlobalState}
          planes={planes}
          studyUID={null}
        /> */}

        {/* Feed Preview Modal */}
        <FeedPreviewModal
          isOpen={showFeedPreviewModal}
          onClose={() => setShowFeedPreviewModal(false)}
          onCropChange={(updatedConfig) => {
            console.log(
              'Updating global state with new crop config:',
              updatedConfig
            );
            setGlobalState((prev) => ({
              ...prev,
              machineConfig: updatedConfig,
            }));
            setMachinesList((prev) =>
              prev.map((m) =>
                m.id.toString() === updatedConfig.id.toString()
                  ? updatedConfig
                  : m
              )
            );
          }}
          onStartExam={async (updatedConfig) => {
            console.log('[onStartExam] Starting exam with:');
            console.log('  selectedFeedType:', selectedFeedType);
            console.log('  selectedFeedSource:', selectedFeedSource);
            console.log('  globalState.deviceId:', globalState.deviceId);
            console.log('  availableFeedSources:', availableFeedSources);

            const finalConfig = updatedConfig || previewMachine;

            // Determine the final device ID with proper fallbacks
            let finalDeviceId;
            if (selectedFeedType === 'USB') {
              // Priority: selectedFeedSource > globalState.deviceId > first available device
              finalDeviceId =
                selectedFeedSource ||
                globalState.deviceId ||
                (availableFeedSources.length > 0
                  ? availableFeedSources[0].deviceId
                  : '');
            } else {
              finalDeviceId = globalState.deviceId || 'screen-share';
            }

            console.log('  finalDeviceId:', finalDeviceId);

            // Update global state
            setGlobalState((prev) => ({
              ...prev,
              machineConfig: finalConfig,
              deviceId: finalDeviceId,
              optionValueDevice: selectedFeedType,
              isStart: true,
              isVideoFrozen: false,
            }));

            // Start video stream if needed
            if (selectedFeedType === 'USB' && finalDeviceId) {
              try {
                console.log(
                  'Starting video stream with device ID:',
                  finalDeviceId
                );
                await startVideoStream(finalDeviceId, 'USB');
              } catch (error) {
                console.error('Failed to start video stream:', error);
              }
            } else if (selectedFeedType === 'Share') {
              try {
                console.log('Starting screen share');
                await startVideoStream('screen-share', 'Share');
              } catch (error) {
                console.error('Failed to start screen share:', error);
              }
            }

            // Close the modal
            setShowDeviceModal(false);
            setShowFeedPreviewModal(false);
          }}
          videoStream={videoStream}
          machineConfig={previewMachine}
          feedType={selectedFeedType}
          autoStartDelay={5}
          machinesList={machinesList}
        />

        {/* Exam type choice (right after stream/feed setup) */}
        <Modal isOpen={showExamTypeChoice} onClose={() => setShowExamTypeChoice(false)} size="xl">
          <Modal.Header title={t('chooseExamType')} onClose={() => setShowExamTypeChoice(false)} />
          <Modal.Body>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-2">
              <button
                type="button"
                onClick={() => { setExamModeTab('realtime'); setChosenExamMode('realtime'); setExamTypeChosen(true); setShowExamTypeChoice(false); }}
                className="text-left rounded-xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:shadow-md transition-all p-5"
              >
                <div className="text-sm font-bold text-slate-900 dark:text-white">{t('mode.realtime')}</div>
                <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  {t('mode.realtimeDescription')}
                </div>
              </button>
              <button
                type="button"
                onClick={() => { setExamModeTab('blindSweep'); setChosenExamMode('blindSweep'); setExamTypeChosen(true); setShowExamTypeChoice(false); }}
                className="text-left rounded-xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:shadow-md transition-all p-5"
              >
                <div className="text-sm font-bold text-slate-900 dark:text-white">{t('mode.blindSweep')}</div>
                <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  {t('mode.blindSweepDescription')}
                </div>
              </button>
            </div>
          </Modal.Body>
        </Modal>

        {/* Report Modal - Shows after exam ends */}
        {/*savedStudyInstanceUID && (
          <ReportMedicalPage
            studyInfo={{ studyId: savedStudyInstanceUID }}
            isOpen={showReportModal}
            onClose={() => setShowReportModal(false)}
          />
        )}*/}

        {/* Patient & Pregnancy Modal - Shows after End Session */}
        <PatientSelectionModal
          isOpen={showPatientPregnancyModal}
          onClose={handlePatientPregnancyClose}
          wizardState={wizardState}
          setWizardState={setWizardState}
          onConfirm={handleConfirmPatientAndEndExam}
          onDirectSaveFromPregnancy={handleDirectSaveFromPregnancyModal}
          isPatientLocked={false}
        />

        {/* Patient & Pregnancy Modal - Blind Sweep tab */}
        <PatientSelectionModal
          isOpen={showBlindPatientModal}
          onClose={() => setShowBlindPatientModal(false)}
          wizardState={wizardState}
          setWizardState={setWizardState}
          onConfirm={handleConfirmPatientForBlindSweep}
          isPatientLocked={false}
        />

        {/* (Blind Sweep detected-views popup removed by request) */}

        {/* MAIN CONTENT AREA */}
        <div className="flex h-full w-full flex-col p-6 bg-card-color-200 dark:bg-gray-900">
          {/* Header */}
          <div className="flex w-full items-center justify-between gap-4">
            <div className="flex items-center space-x-4 min-w-0">
              {/* Mode tabs */}
              <div className="inline-flex rounded-xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden shadow-sm">
                <button
                  type="button"
                  onClick={() => {
                    if (chosenExamMode && chosenExamMode !== 'realtime') return;
                    setExamModeTab('realtime');
                  }}
                  disabled={!!chosenExamMode && chosenExamMode !== 'realtime'}
                  className={`px-3 py-1.5 text-xs font-bold transition-colors ${
                    examModeTab === 'realtime'
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-600 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-gray-700'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {t('mode.realtime')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (chosenExamMode && chosenExamMode !== 'blindSweep') return;
                    setExamModeTab('blindSweep');
                  }}
                  disabled={!!chosenExamMode && chosenExamMode !== 'blindSweep'}
                  className={`px-3 py-1.5 text-xs font-bold transition-colors ${
                    examModeTab === 'blindSweep'
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-600 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-gray-700'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {t('mode.blindSweep')}
                </button>
              </div>

              <div
                className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium border shadow-sm ${isSessionActive &&
                  !globalState.isExamEnded &&
                  !globalState.isVideoFrozen
                  ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800'
                  : isSessionActive &&
                    !globalState.isExamEnded &&
                    globalState.isVideoFrozen
                    ? 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800'
                    : globalState.isExamEnded
                      ? 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-400 border-gray-200 dark:border-gray-700'
                      : 'bg-slate-50 dark:bg-gray-800 text-slate-600 dark:text-gray-400 border-slate-200 dark:border-gray-700'
                  }`}
              >
                <div
                  className={`w-2 h-2 rounded-full ${isSessionActive &&
                    !globalState.isExamEnded &&
                    !globalState.isVideoFrozen
                    ? 'bg-green-500 animate-pulse'
                    : isSessionActive &&
                      !globalState.isExamEnded &&
                      globalState.isVideoFrozen
                      ? 'bg-yellow-500'
                      : 'bg-gray-400'
                    }`}
                ></div>
                <span>
                  {globalState.isExamEnded
                    ? t('status.ended')
                    : isSessionActive
                      ? globalState.isVideoFrozen
                        ? t('status.paused')
                        : t('status.active')
                      : t('status.notStarted')}
                </span>
              </div>

              {/* Connection Status Indicator */}
              {globalState?.isConnected ? (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                  <div className="relative flex h-2 w-2">
                    <div className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></div>
                    <div className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></div>
                  </div>
                  <span className="text-xs font-medium text-green-700 dark:text-green-400">
                    {t('status.connected')}
                  </span>
                </div>
              ) : (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                  <div className="h-2 w-2 rounded-full bg-red-500 animate-pulse"></div>
                  <span className="text-xs font-medium text-red-700 dark:text-red-400">
                    {t('status.disconnected')}
                  </span>
                </div>
              )}

              {/* Feed Selection in Header - Shows when exam is paused */}
              {isSessionActive &&
                globalState.isVideoFrozen &&
                !globalState.isExamEnded && (
                  <div className="flex items-center gap-3">
                    {/* Feed & Crop Selection with Two-Panel Interface */}
                    <div className="relative feed-dropdown-container">
                      <button
                        onClick={() => setShowFeedDropdown(!showFeedDropdown)}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                        disabled={isApplyingFeedChange}
                      >
                        <span className="text-gray-900 dark:text-gray-100">
                          {(() => {
                            const machine = machinesList.find(
                              (m) =>
                                m.id.toString() === selectedMachineForChange
                            );
                            if (machine) {
                              return `${machine.name} - ${machine.crop_width} x ${machine.crop_height}`;
                            }
                            return t('selectFeedAndCrop');
                          })()}
                        </span>
                        <ChevronDown
                            className={`w-4 h-4 transition-transform ${showFeedDropdown ? 'rotate-180' : ''}`}
                          />
                      </button>

                      {showFeedDropdown && (
                        <div className="absolute top-full left-0 mt-1 flex bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-50">
                          {/* Left Panel - USB Feed Sources */}
                          <div className="w-[280px] max-h-[400px] overflow-y-auto">
                            {availableFeedSources.length > 0 ? (
                              <>
                                <div className="bg-gray-100 dark:bg-gray-700 px-3 py-2 sticky top-0 z-10">
                                  <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                                    {t('feed.usbFeeds')}
                                  </span>
                                </div>
                                {availableFeedSources.map((source) => {
                                  const cropsCount = machinesList.filter(
                                    (m) => m.name === source.label
                                  ).length;
                                  const isSelected =
                                    selectedFeedSource === source.deviceId;

                                  return (
                                    <button
                                      key={source.deviceId}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedFeedSource(source.deviceId);
                                        setSelectedFeedType('USB');
                                      }}
                                      className={`w-full text-left p-4 transition-colors border-b border-gray-100 dark:border-gray-600 last:border-b-0 ${isSelected
                                        ? 'bg-blue-50 dark:bg-gray-700'
                                        : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                        }`}
                                    >
                                      <div className="flex items-center justify-between gap-2">
                                        <div className="flex-1 min-w-0">
                                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                            {source.label}
                                          </div>
                                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                            {t('feed.cropCount', { count: cropsCount })}
                                          </div>
                                        </div>
                                        <span className="px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full flex-shrink-0">
                                          USB
                                        </span>
                                      </div>
                                    </button>
                                  );
                                })}
                              </>
                            ) : (
                              <div className="p-4 text-center">
                                <p className="text-gray-500 dark:text-gray-400 text-sm mb-3">
                                  {t('feed.noUsbCameras')}
                                </p>
                                <button
                                  onClick={async () => {
                                    try {
                                      const stream =
                                        await navigator.mediaDevices.getUserMedia(
                                          { video: true }
                                        );
                                      stream
                                        .getTracks()
                                        .forEach((track) => track.stop());
                                      const devices =
                                        await navigator.mediaDevices.enumerateDevices();
                                      const videoDevices = devices.filter(
                                        (device) => device.kind === 'videoinput'
                                      );
                                      if (videoDevices.length > 0) {
                                        window.location.reload();
                                      }
                                    } catch (error) {
                                      console.error(
                                        'Failed to request camera permission:',
                                        error
                                      );
                                      alert(t('feed.cameraPermissionDenied'));
                                    }
                                  }}
                                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md transition-colors"
                                >
                                  {t('feed.requestCameraPermission')}
                                </button>
                              </div>
                            )}
                          </div>

                          {/* Right Panel - Crop Configurations (shown only when feed is clicked/selected) */}
                          <div
                            className={`${selectedFeedSource ? 'w-[280px]' : 'w-0'} overflow-hidden transition-all duration-200 border-l border-gray-200 dark:border-gray-600`}
                            style={{
                              display: selectedFeedSource ? 'block' : 'none',
                            }}
                          >
                            {selectedFeedSource && (
                              <div className="max-h-[400px] overflow-y-auto">
                                <div className="bg-gray-100 dark:bg-gray-700 px-3 py-2 sticky top-0 z-10">
                                  <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                                    {t('feed.cropConfigurations')}
                                  </span>
                                </div>
                                {machinesList
                                  // .filter(m => m.name === availableFeedSources.find(s => s.deviceId === selectedFeedSource)?.label)
                                  .map((machine) => (
                                    <button
                                      key={machine.id}
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        setSelectedFeedType('USB');
                                        setSelectedMachineForChange(
                                          machine.id.toString()
                                        );
                                        setShowFeedDropdown(false);

                                        // Start video stream and open preview modal
                                        try {
                                          setIsApplyingFeedChange(true);
                                          const deviceIdToUse =
                                            selectedFeedSource ||
                                            globalState.deviceId ||
                                            (availableFeedSources.length > 0
                                              ? availableFeedSources[0].deviceId
                                              : '');

                                          if (deviceIdToUse) {
                                            await startVideoStream(
                                              deviceIdToUse,
                                              'USB'
                                            );
                                          }

                                          await new Promise((resolve) =>
                                            setTimeout(resolve, 500)
                                          );

                                          setPreviewMachine(machine);
                                          setShowFeedPreviewModal(true);
                                        } catch (error) {
                                          console.error(
                                            'Failed to start preview stream:',
                                            error
                                          );
                                          alert(
                                            'Failed to start video preview. Please check your camera connection.'
                                          );
                                        } finally {
                                          setIsApplyingFeedChange(false);
                                        }
                                      }}
                                      className="w-full text-left px-4 py-3 hover:bg-blue-50 dark:hover:bg-gray-700 transition-colors border-b border-gray-100 dark:border-gray-600 last:border-b-0"
                                    >
                                      <div className="flex flex-col gap-1">
                                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                          {machine.crop_width} x{' '}
                                          {machine.crop_height}
                                        </span>
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                          Position: ({machine.crop_x},{' '}
                                          {machine.crop_y})
                                        </span>
                                        {machine.id.toString() ===
                                          globalState.machineConfig?.id?.toString() && (
                                            <span className="inline-flex items-center px-2 py-0.5 bg-green-600 text-white text-xs rounded-full w-fit">
                                              {t('feed.currentCrop')}
                                            </span>
                                          )}
                                      </div>
                                    </button>
                                  ))}
                                {machinesList.filter(
                                  (m) =>
                                    m.name ===
                                    availableFeedSources.find(
                                      (s) => s.deviceId === selectedFeedSource
                                    )?.label
                                ).length === 0 && (
                                    <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400 italic">
                                      {t('feed.noCropConfigurations')}
                                    </div>
                                  )}
                              </div>
                            )}
                          </div>

            {/* (Blind Sweep actions are shown in the top-right header buttons area) */}
                        </div>
                      )}
                    </div>

                    {/* New Crop Button */}
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        setResetStepperOnOpen(true);
                        setShowDeviceModal(true);
                      }}
                      disabled={isApplyingFeedChange}
                    >
                      {t('feed.newCrop')}
                    </Button>
                  </div>
                )}
            </div>
            <div className="flex items-center space-x-2">
              {/* Blind Sweep action buttons inline (sweep numbers remain in their normal place in the Blind Sweep panel) */}
              {examModeTab === 'blindSweep' && (() => {
                const s = blindSweeps.find((x) => x.id === blindActiveSweep);
                const recording = s?.state === 'recording';
                const firstIncomplete = blindSweeps.find((x) => x.state !== 'done')?.id ?? BLIND_NUM_SWEEPS;
                const isAllowedSweep = blindActiveSweep <= firstIncomplete;
                const canRecord = isSessionActive && !!videoStream && !creatingBlindStudy && !recording;
                return (
                  <>
                    <button
                      type="button"
                      disabled={!canRecord || !isAllowedSweep}
                      onClick={() => startBlindSweepRecording(blindActiveSweep)}
                      className={`px-4 py-2 rounded-xl text-sm font-bold transition-colors ${
                        canRecord && isAllowedSweep ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-slate-200 text-slate-500 dark:bg-gray-700 dark:text-gray-400'
                      }`}
                    >
                      {recording
                        ? t('recordingSeconds', { seconds: s?.secondsLeft ?? 10 })
                        : t('record10s')}
                    </button>
                    <button
                      type="button"
                      disabled={!recording}
                      onClick={() => (blindIsPaused ? resumeBlindRecording() : pauseBlindRecording())}
                      className={`px-4 py-2 rounded-xl text-sm font-bold transition-colors ${
                        recording ? 'bg-amber-500 text-white hover:bg-amber-600' : 'bg-slate-200 text-slate-500 dark:bg-gray-700 dark:text-gray-400'
                      }`}
                      title={t('pauseResumeSweepTitle')}
                    >
                      {blindIsPaused ? t('resume') : t('pause')}
                    </button>
                    <button
                      type="button"
                      disabled={!recording}
                      onClick={stopBlindRecording}
                      className={`px-4 py-2 rounded-xl text-sm font-bold transition-colors ${
                        recording ? 'bg-slate-900 text-white hover:bg-black' : 'bg-slate-200 text-slate-500 dark:bg-gray-700 dark:text-gray-400'
                      }`}
                    >
                      {t('stop')}
                    </button>
                    <button
                      type="button"
                      disabled={!blindIsFinished}
                      onClick={handleFinishBlindSweepExam}
                      className={`px-4 py-2 rounded-xl text-sm font-bold transition-colors ${
                        blindIsFinished ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-slate-200 text-slate-500 dark:bg-gray-700 dark:text-gray-400'
                      }`}
                    >
                      {t('finish')}
                    </button>
                    <button
                      type="button"
                      disabled={blindSweeps.every((x) => !x.blobUrl)}
                      onClick={() => {
                        const firstWithVideo = blindSweeps.find((x) => x.blobUrl)?.id ?? 1;
                        setBlindVideoSweepId(firstWithVideo);
                        setShowBlindVideosModal(true);
                      }}
                      className="px-4 py-2 rounded-xl text-sm font-bold border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
                      title={t('reviewSweepVideosTitle')}
                    >
                      {t('videos')}
                    </button>
                  </>
                );
              })()}

              {/* Unified Start/Pause/Resume Button (Realtime only) */}
              {examModeTab === 'realtime' &&
                isSessionActive &&
                !globalState.isExamEnded &&
                globalState?.isConnected && (
                  <Button
                    variant="primary"
                    size="sm"
                    className="px-5 font-semibold"
                    onClick={async () => {
                      if (!globalState.isStart || globalState.isVideoFrozen) {
                        try {
                          const isStreamActive =
                            videoStream && videoStream.active;

                          if (!isStreamActive && globalState.deviceId) {
                            if (globalState.optionValueDevice === 'USB') {
                              await startVideoStream(
                                globalState.deviceId,
                                'USB'
                              );
                            } else if (
                              globalState.optionValueDevice === 'Share'
                            ) {
                              await startVideoStream('screen-share', 'Share');
                            }
                            await new Promise((resolve) =>
                              setTimeout(resolve, 500)
                            );
                          }

                          // Start or Resume
                          setGlobalState((prevState) => ({
                            ...prevState,
                            isStart: true,
                            isVideoFrozen: false,
                          }));
                        } catch (error) {
                          console.error(error);
                          messageI18n.error('realtime.videoStreamStartFailedCamera');
                        }
                      } else {
                        handlePauseResume();
                      }
                    }}
                  >
                    {!globalState.isStart || globalState.isVideoFrozen ? (
                      <>
                        <FaPlay className="mr-2" />
                        {!globalState.isStart ? t('startExam') : t('resume')}
                      </>
                    ) : (
                      <>
                        <FaPause className="mr-2" />
                        {t('pause')}
                      </>
                    )}
                  </Button>
                )}

              {/* End Session (Realtime only) */}
              {examModeTab === 'realtime' &&
                isSessionActive &&
                !globalState.isExamEnded &&
                globalState?.isConnected && (
                  <Button
                    variant="destructive"
                    size="sm"
                    className="px-5 font-semibold"
                    onClick={handleEndSession}
                    disabled={
                      !Object.keys(globalState.planes).some(
                        (tag) =>
                          globalState?.planes[tag]?.predictionHistory?.length >
                          0
                      )
                    }
                  >
                    <FaHourglassEnd className="mr-2" /> {t('endSession')}
                  </Button>
                )}

              {/* Post-exam buttons */}
              {examModeTab === 'realtime' && isSessionActive && globalState.isExamEnded && (
                <>
                  {/* Loading indicator when saving */}
                  {saveViewsLoading && (
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 shadow-sm">
                      <FaSpinner
                        className="animate-spin text-blue-600 dark:text-blue-400"
                        size={14}
                      />
                      <span className="text-sm font-medium text-blue-700 dark:text-blue-400">
                        {t('savingExamData')}
                      </span>{' '}
                    </div>
                  )}

                  {/* Reset - Start New Exam */}
                  <Button
                    variant="secondary"
                    size="sm"
                    className="px-5 font-semibold"
                    onClick={() => {
                      cleanupBlobUrls(globalState.planes);

                      // Reset exam state but keep device configuration
                      setGlobalState((prevState) => ({
                        ...prevState,
                        isExamEnded: false,
                        isVideoFrozen: true,
                        isStart: false,
                        lastDetectedPlane: '',
                        planes: {},
                        pixelSpacingMap: {},
                        patient: null,
                        pregnancy: null,
                        viewList: [],
                        viewDetectionOrder: [],
                        reportModalOpened: false,
                        receivedData: null,
                      }));
                      setWizardState({
                        patient: null,
                        pregnancy: null,
                        extractedPatients: [],
                      });
                      setSavedStudyInstanceUID(null);
                      setExamDuration(realtimeExamDuration);
                      setIsChecked(false);
                    }}
                    disabled={saveViewsLoading}
                  >
                    <FaPlay className="mr-2" /> {t('resetAndNewExam')}
                  </Button>

                  {/* AI Accelerate - Navigate to AI accelerate page */}
                  <Button
                    variant="primary"
                    size="sm"
                    className="px-5 font-semibold"
                    onClick={() => {
                      if (savedStudyInstanceUID) {
                        navigate(
                          `/exam/viewer?StudyInstanceUIDs=${savedStudyInstanceUID}`
                        );
                      } else {
                        messageI18n.warning('realtime.studyNotSavedYet');
                      }
                    }}
                    disabled={saveViewsLoading || !savedStudyInstanceUID}
                  >
                    <IoIosFlash className="mr-2" /> {t('aiAccelerate')}
                  </Button>

                  {/* View Exam - Navigate to exam viewer */}
                  <Button
                    variant="dark"
                    size="sm"
                    className="px-5 font-semibold"
                    onClick={() => {
                      if (savedStudyInstanceUID) {
                        navigate(
                          `/exams/viewer?StudyInstanceUIDs=${savedStudyInstanceUID}`
                        );
                      } else {
                        messageI18n.warning('realtime.studyNotSavedYet');
                      }
                    }}
                    disabled={saveViewsLoading || !savedStudyInstanceUID}
                  >
                    <MdOutlineDashboard className="mr-2" /> {t('viewExam')}
                  </Button>

                  {/* Report - Show report modal */}
                  <Button
                    variant="secondary"
                    size="sm"
                    className="px-5 font-semibold"
                    onClick={() => {
                      if (savedStudyInstanceUID) {
                        setShowReportModal(true);
                      } else {
                        messageI18n.warning('realtime.studyNotSavedYet');
                      }
                    }}
                    disabled={saveViewsLoading || !savedStudyInstanceUID}
                  >
                    <FiFileText className="mr-2" /> {t('report')}
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Progress Bar
                    <div className="my-4 rounded-lg bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 p-5 shadow-sm">
                        <div className="flex justify-between items-center mb-3">
                            <div className="flex items-center gap-3">
                                <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 bg-blue-500 dark:bg-blue-400 rounded-full"></div>
                                    <span className="text-sm font-semibold text-gray-800 dark:text-white uppercase tracking-wide">
                                        Scan Progress
                                    </span>
                                </div>
                                {!globalState.isExamEnded &&
                                    isSessionActive &&
                                    !globalState.isVideoFrozen && (
                                        <div className="flex items-center gap-2 px-2 py-1 rounded-full bg-blue-50 dark:bg-blue-900/30">
                                            <FaSpinner
                                                size={12}
                                                className="animate-spin text-blue-600 dark:text-blue-400"
                                            />
                                            <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                                                Scanning...
                                            </span>
                                        </div>
                                    )}
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                                    {planesWithFrameCount}
                                </span>
                                <span className="text-sm text-gray-500 dark:text-gray-400">
                                    / 23 Views
                                </span>
                            </div>
                        </div>
                        <div className="w-full bg-slate-100 dark:bg-gray-700 rounded-full h-2 overflow-hidden shadow-inner">
                            <div
                                className="bg-gradient-to-r from-blue-500 to-blue-600 dark:from-blue-500 dark:to-blue-600 h-full rounded-full transition-all duration-500 ease-out shadow-sm"
                                style={{ width: `${(planesWithFrameCount / 23) * 100}%` }}
                            ></div>
                        </div>
                        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 text-right">
                            {Math.round((planesWithFrameCount / 23) * 100)}% Complete
                        </div>
                    </div> */}

          {/* Essential hidden elements for video processing. */}
          {/* Keep these offscreen (not display:none) so video metadata loads reliably. */}
          <div
            style={{
              // Keep it inside viewport; some browsers stop decoding if far offscreen.
              position: 'fixed',
              left: 0,
              top: 0,
              width: 1,
              height: 1,
              overflow: 'hidden',
              opacity: 0,
              pointerEvents: 'none',
            }}
          >
            <video ref={videoRef} autoPlay playsInline muted />
            <canvas ref={canvasRef} />
            <canvas ref={modalCanvasVideoRef} />
          </div>

          {/* Main content */}
          {examModeTab === 'realtime' ? (
            /* View Cards Grid */
            <div className="flex-grow overflow-y-auto custom-scrollbar -mr-4 pr-4 pt-4">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8">
                {planes.map((plane, index) => {
                const hasHistory =
                  !!globalState?.planes?.[plane?.tag]?.predictionHistory;
                const foundImageSrc = hasHistory
                  ? globalState?.planes?.[plane?.tag]?.predictionHistory?.[0]
                    ?.imagePredictedPlane
                  : null;

                return (
                  <div
                    key={index}
                    className={`group relative aspect-square overflow-hidden rounded-lg border transition-all duration-200 ${hasHistory
                      ? 'bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-700 shadow-sm'
                      : 'bg-slate-50 dark:bg-gray-800/50 border-slate-300 dark:border-gray-600'
                      } ${hasHistory
                        ? 'cursor-pointer hover:shadow-md hover:border-blue-400 dark:hover:border-blue-500 hover:-translate-y-0.5'
                        : 'cursor-not-allowed'
                      }`}
                    onClick={() => {
                      if (hasHistory) {
                        setSelectedPlansModal(plane?.label);
                        setSelectedPlansTag(plane?.tag);
                      }
                    }}
                  >
                    {/* STATE: PENDING (NOT FOUND) */}
                    <div
                      className={`absolute inset-0 flex flex-col items-center justify-between p-2 sm:p-3 text-center transition-opacity duration-500 ${hasHistory
                        ? 'opacity-0 pointer-events-none'
                        : 'opacity-100'
                        }`}
                    >
                      <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-slate-400 dark:bg-gray-500 shadow-sm"></div>

                      <div className="flex items-center justify-center w-full h-full">
                        <img
                          src={plane.imgPlane}
                          className="max-w-[90%] max-h-[90%] object-contain opacity-40 dark:opacity-30"
                          alt={plane.label}
                        />
                      </div>
                      <div className="w-full flex-shrink-0">
                        <p className="text-xs font-semibold text-slate-700 dark:text-gray-300 line-clamp-2 px-1">
                          {plane.label}
                        </p>
                      </div>
                    </div>

                    {/* STATE: PLANE FOUND (ACTIVATED) */}
                    {hasHistory && (
                      <div
                        className={`absolute inset-0 transition-opacity duration-500 ${hasHistory ? 'opacity-100' : 'opacity-0'
                          }`}
                      >
                        <img
                          src={foundImageSrc}
                          className="h-full w-full object-contain bg-black transition-transform duration-300 ease-in-out group-hover:scale-[1.02]"
                          alt={plane.label}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent"></div>
                        <div className="absolute inset-0 p-2 sm:p-3 flex flex-col justify-between">
                          <div className="flex justify-between items-start gap-1 sm:gap-2">
                            <span className="font-semibold text-white text-xs sm:text-xs leading-tight drop-shadow-lg flex-1 line-clamp-2">
                              {plane.label}
                            </span>
                            <div className="flex items-center gap-0.5 sm:gap-1 rounded-full bg-blue-500/90 dark:bg-blue-600/90 backdrop-blur-sm px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs font-bold text-white shadow-lg flex-shrink-0">
                              <TfiLayers size={9} />
                              <span className="text-xs">
                                {
                                  globalState?.planes?.[plane?.tag]
                                    ?.predictionHistory?.length
                                }
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="inline-flex items-center gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full bg-green-500/90 dark:bg-green-600/90 backdrop-blur-sm shadow-lg">
                              <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></div>
                              <span className="text-xs font-semibold text-white">
                                {t('planes.detected')}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
                })}
              </div>
            </div>
          ) : (
            /* Blind Sweep tab placeholder: UI + recorder will be added next */
            <div className="flex-grow pt-4">
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-4 h-full min-h-0">
                {/* Segmented preview (no raw live video) */}
                <div className="rounded-2xl border border-slate-200 dark:border-gray-800 bg-black overflow-hidden relative min-h-[320px] flex items-center justify-center">
                  {blindSelectedFrame ? (
                    <img
                      src={blindSelectedFrame}
                      className="w-full h-full object-contain"
                      alt="segmented-preview"
                    />
                  ) : (
                    <div className="text-white/80 text-sm px-4 text-center">
                      {blindIsRecording ? t('waitingSegmentedFrames') : t('pressRecordToStart')}
                    </div>
                  )}
                </div>

                {/* Guidance + sweeps */}
                <div className="rounded-2xl border border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 flex flex-col min-h-0">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-xs font-semibold text-slate-500 dark:text-slate-400">{t('mode.blindSweep')}</div>
                      <div className="text-base font-bold text-slate-900 dark:text-white">
                        {t('sweepProgress', { current: blindActiveSweep, total: BLIND_NUM_SWEEPS })}
                      </div>
                      <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                        {t('blindSweepGuide')}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      {/* Patient selection handled on Finish & Review */}
                    </div>
                  </div>

                  {/* Sweep guide (same as upload reference images) */}
                  <div className="mt-4 rounded-xl border border-slate-200 dark:border-gray-800 bg-slate-50 dark:bg-gray-800 p-3">
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-semibold text-slate-600 dark:text-slate-300">{t('scanLine', { line: blindActiveSweep - 1 })}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">10s</div>
                    </div>

                    <div className="mt-2 flex items-center justify-center">
                      <div className="relative w-full h-64 sm:h-72 md:h-80 lg:h-96">
                        {/* Title pill (same intent as upload overlay) */}
                        <div className="absolute top-2 left-2 z-10 text-[11px] font-bold px-2 py-0.5 rounded-md bg-white/90 text-slate-800 shadow-sm border border-slate-200">
                          {t('sweepLabel', { id: blindGuideSweepId })}
                        </div>

                        <div className="h-full w-full rounded-lg border-2 border-white bg-white bg-opacity-70 shadow-md">
                          <BlindSweepGuideAnimation
                            animationClass={
                              sweepById.get(blindGuideSweepId)?.animationClass ??
                              'ultrasound-sweep-1'
                            }
                          />
                        </div>

                        {/* Instructions are shown via Info popup */}
                      </div>
                    </div>

                    {/* Sweeps selector (directly under animation) */}
                    <div className="mt-3">
                      <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2">
                        {t('sweeps')}
                      </div>
                      <div className="grid grid-cols-7 gap-1.5">
                        {blindSweeps.map((s) => (
                          <button
                            key={s.id}
                            type="button"
                            disabled={
                              (() => {
                                const firstIncomplete =
                                  blindSweeps.find((x) => x.state !== 'done')?.id ?? BLIND_NUM_SWEEPS;
                                // Allow selecting only completed sweeps and the next required sweep.
                                return s.id > firstIncomplete;
                              })()
                            }
                            onClick={() => setBlindActiveSweep(s.id)}
                            className={`h-9 rounded-lg border text-[11px] font-bold transition-all ${
                              s.id === blindActiveSweep
                                ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300'
                                : 'border-slate-200 bg-white text-slate-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300'
                            } disabled:opacity-40 disabled:cursor-not-allowed`}
                            title={t('sweepStateTitle', { id: s.id, state: s.state })}
                          >
                            {s.id}
                          </button>
                        ))}
                      </div>
                      <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                        {t('sweepsCompleted', {
                          done: blindSweeps.filter((s) => s.state === 'done').length,
                          total: BLIND_NUM_SWEEPS,
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Required CSS animations (upload design, but centered + correct probe orientation) */}
                  <style>{`.path-anim { animation: draw-path 3s ease-in-out infinite alternate; } @keyframes draw-path { from { stroke-dashoffset: 100; } to { stroke-dashoffset: 0; } }

/* Probe orientation rule:
   - moving VERTICAL => probe HORIZONTAL (rotate 0deg)
   - moving HORIZONTAL => probe VERTICAL (rotate 90deg)
   Note: probe is centered via inner translate(-50%,-50%) in SweepAnimation.
*/

@keyframes sweep-1 {
  /* bottom -> mid (vertical movement) => probe horizontal */
  0%   { left: 50%; top: 92%; transform: rotate(0deg); opacity: 1; }
  30%  { left: 50%; top: 70%; transform: rotate(0deg); opacity: 1; }
  /* rotate to vertical while invisible */
  /* Since the probe is centered (translate -50%/-50%), nudge endpoints inward
     so the probe visually starts/ends exactly on the dashed segment. */
  35%  { left: 82%; top: 70%; transform: rotate(90deg); opacity: 0; }
  40%  { left: 82%; top: 70%; transform: rotate(90deg); opacity: 1; }
  /* right -> left (horizontal movement) => probe vertical */
  100% { left: 18%; top: 70%; transform: rotate(90deg); opacity: 1; }
}
.ultrasound-sweep-1 { animation: sweep-1 4s ease-in-out infinite alternate; }

@keyframes sweep-v { 0% { top: 45%; opacity: 0.8; transform: rotate(0deg)} 100% { top: 92%; opacity: 1; transform: rotate(0deg)} }
.ultrasound-sweep-2 { left: 25%; animation: sweep-v 2s ease-in-out infinite alternate; }
.ultrasound-sweep-3 { left: 50%; animation: sweep-v 2s ease-in-out infinite alternate 0.3s; }
.ultrasound-sweep-4 { left: 75%; animation: sweep-v 2s ease-in-out infinite alternate 0.6s; }

@keyframes sweep-h { 0% { left: 15%; opacity: 0.8; transform: rotate(90deg); } 100% { left: 85%; opacity: 1; transform: rotate(90deg); } }
.ultrasound-sweep-5 { top: 50%; animation: sweep-h 2.5s ease-in-out infinite alternate; }
.ultrasound-sweep-6 { top: 75%; animation: sweep-h 2.5s ease-in-out infinite alternate 0.5s; }

@keyframes sweep-c { 0% { left: 20%; top: 80%; transform: rotate(90deg); } 50% { left: 50%; top: 90%; transform: rotate(90deg); } 100% { left: 80%; top: 80%; transform: rotate(90deg); } }
.ultrasound-sweep-7 { animation: sweep-c 3s ease-in-out infinite alternate; }`}</style>

                  {/* No custom animation here: use upload guide images as-is. */}

                  {/* Segmented frames strip (click to browse) */}
                  <div className="mt-4 pt-4 border-t border-slate-200 dark:border-gray-800">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                        {t('segmentedFrames')}
                      </div>
                      <div className="text-[10px] text-slate-400">
                        {blindIsRecording ? t('recordingLatestFrames') : t('startRecordingToCapture')}
                      </div>
                    </div>
                    <div className="flex gap-2 overflow-x-auto custom-scrollbar pb-1">
                      {activeSweepFrames.length === 0 ? (
                        <div className="text-xs text-slate-400">{t('noFramesYet')}</div>
                      ) : (
                        activeSweepFrames.map((src, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => {
                              setBlindAutoFollowLatest(false);
                              setBlindSelectedFrame(src.src);
                              setBlindSelectedFrameInfo(src);
                            }}
                            className={`relative w-24 h-16 flex-shrink-0 rounded-lg overflow-hidden border bg-black transition ${
                              blindSelectedFrame === src.src
                                ? 'border-blue-500 ring-2 ring-blue-500/30'
                                : 'border-slate-200 dark:border-gray-700 hover:border-blue-400'
                            }`}
                            title={t('showThisFrame')}
                          >
                            <img src={src.src} className="absolute inset-0 w-full h-full object-cover" alt={`frame-${i}`} />
                            <div className="absolute bottom-1 right-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-black/60 text-white">
                              {src.views?.length ?? 0}
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>

                  {/* (Detected views panel removed by request) */}
                </div>
              </div>
            </div>
          )}

          <Modal
            isOpen={showBlindVideosModal}
            onClose={() => setShowBlindVideosModal(false)}
            size="5xl"
            className="lg:h-[90vh]"
          >
            <Modal.Header title={t('recordedSweeps')} onClose={() => setShowBlindVideosModal(false)} />
            <Modal.Body className="p-4 lg:p-6">
              <div className="flex flex-wrap gap-2">
                {blindSweeps.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    disabled={!s.blobUrl}
                    onClick={() => setBlindVideoSweepId(s.id)}
                    className={`px-2.5 py-1 rounded-md text-xs font-bold border transition ${
                      s.id === blindVideoSweepId
                        ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-blue-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300'
                    } disabled:opacity-40`}
                  >
                    {t('sweepLabel', { id: s.id })}
                  </button>
                ))}
              </div>

              <div className="mt-4 rounded-xl border border-slate-200 dark:border-gray-700 bg-black overflow-hidden h-[70vh]">
                {(() => {
                  const s = blindSweeps.find((x) => x.id === blindVideoSweepId);
                  if (!s?.blobUrl) return <div className="text-white/70 text-sm p-4">{t('noVideoForSweep')}</div>;
                  return (
                    <video
                      key={s.blobUrl}
                      src={s.blobUrl}
                      controls
                      playsInline
                      preload="metadata"
                      className="w-full h-full object-contain"
                    />
                  );
                })()}
              </div>
            </Modal.Body>
          </Modal>

        </div>
      </div>
    </>
  );
};

const RealtimeExamPageWithProvider: React.FC = () => {
  return (
    <RealtimeGlobalStateProvider>
      <RealtimeExamPage />
    </RealtimeGlobalStateProvider>
  );
};

export default RealtimeExamPageWithProvider;
